import {
  useAudioManager,
  useMapManager,
  useNewsManager,
  useTimeoutManager,
  useTtsQueueManager,
  useWakewordManager,
} from "./components";
import { useTransportManager } from "./hooks/useTransportManager";
import { PhoneSimSettings } from "./components/PhoneSimSettings";
import {
  FilterConfig,
  FilterMode,
  profileToConfig,
} from "../../utils/telephonyFilters";
import { clientLogger, installLifecycleHooks } from "../../utils/clientLogger";
import {
  DEFAULT_STT_MODEL,
  STT_MODEL_OPTIONS,
  normalizeSttModel,
  readStoredSttModel,
  sttModelBadge,
  writeStoredSttModel,
} from "./sttModelOptions";
import {
  ConnectionNotification,
  CountdownEffect,
  LanguageSelector,
  Map,
  News,
  Notification,
} from "./ui";
import {
  AudioMutedOutlined,
  AudioOutlined,
  BookOutlined,
  CheckCircleOutlined,
  CustomerServiceOutlined,
  DownOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  SettingOutlined,
  ShareAltOutlined,
  StarOutlined,
  ToolOutlined,
  UpOutlined,
  WifiOutlined,
  UserOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { useIsMobile } from "@shared/lib";
import { Popover } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styled from "styled-components";

const TESTER_NAME_KEY = "voicebot_tester_name";
const LANGUAGE_CHOSEN_KEY = "voicebot_language_chosen";
const SHARED_BY_PARAM = "shared_by";
const LANG_PARAM = "lang";
const SCENARIO_PARAM = "scenario";
const USE_CASE_PARAM = "use_case";
const SUPPORTED_LANGS = new Set(["vi", "km", "en"]);
const VALID_SCENARIOS = new Set([
  "ha_scenario",
  "technical_issue_scenario",
  "connection_scenario",
  "wifi_survey_scenario",
]);

const readSharedByParam = (): string => {
  try {
    const params = new URLSearchParams(window.location.search);
    return (params.get(SHARED_BY_PARAM) || "").trim();
  } catch {
    return "";
  }
};

const readLangParam = (): string => {
  try {
    const params = new URLSearchParams(window.location.search);
    const lang = (params.get(LANG_PARAM) || "").trim().toLowerCase();
    return SUPPORTED_LANGS.has(lang) ? lang : "";
  } catch {
    return "";
  }
};

const readScenarioParam = (): string => {
  try {
    const params = new URLSearchParams(window.location.search);
    const s = (params.get(SCENARIO_PARAM) || "").trim();
    return VALID_SCENARIOS.has(s) ? s : "";
  } catch {
    return "";
  }
};

const readUseCaseParam = (): string => {
  try {
    const params = new URLSearchParams(window.location.search);
    return (params.get(USE_CASE_PARAM) || "").trim();
  } catch {
    return "";
  }
};

const pickRandom = <T,>(list: readonly T[]): T => list[Math.floor(Math.random() * list.length)];

// Resolve backend HTTP base URL. Mirrors WebSocketAudioManager.getWebSocketUrl —
// if env points to localhost but the page is served from a real host, fall back
// to the page origin so deployments behind a reverse proxy work without rebuild.
const resolveBackendUrl = (): string => {
  const envUrl = import.meta.env.VITE_BACKEND_URL || '';
  if (envUrl.includes("localhost") && !window.location.hostname.includes("localhost")) {
    return window.location.origin;
  }
  return envUrl;
};

// Constants
const BREAKPOINT_MOBILE = "768px";
const COLORS = {
  primary: "#d97706",
  primaryHover: "#b45309",
  orange: "#f59e0b",
  orangeHover: "#d97706",
  white: "#fff",
  black: "#000",
  gray: "#78716c",
  textPrimary: "#451a03",
  textSecondary: "#78716c",
  gradient: "linear-gradient(135deg, #fef3c7, #fde68a, #fcd34d)",
  backgroundGradient: "linear-gradient(rgb(255, 249, 230) 0%, rgb(255, 254, 245) 50%, rgb(255, 255, 255) 100%)",
  cardBackground: "rgba(255, 255, 255, 0.95)",
  borderColor: "rgba(217, 119, 6, 0.2)",
};
const SPACING = {
  xs: "8px",
  sm: "16px",
  md: "24px",
  lg: "40px",
};
const SHADOWS = {
  text: "0 2px 4px rgba(69, 26, 3, 0.1), 0 4px 8px rgba(217, 119, 6, 0.15)",
  card: "0 4px 15px rgba(217, 119, 6, 0.1)",
};

// WebSocket Event Constants
const WS_EVENTS = {
  // Map events
  MAP_ENABLED: "map_enabled",
  MAP_DISABLED: "map_disabled",
  NAVIGATION_SET: "navigation_set",
  ADDRESS_LIST_SHOWN: "address_list_shown",

  // News events
  NEWS_ENABLED: "news_enabled",
  NEWS_DISABLED: "news_disabled",
  NEWS_PAUSED: "news_paused",
  NEWS_RESUMED: "news_resumed",
  NEWS_NEXT_COMPLETED: "news_next_completed",

  // Bot events
  LISTENING: "listening",
  LISTENING_STOP: "listening_stop",
} as const;

// Tool Constants
const MAP_TOOLS = {
  NAVIGATE_TO_ADDRESS: "navigate_to_address",
  OPEN_MAP: "open_map",
  CLOSE_MAP: "close_map",
  SHOW_ADDRESS_LIST: "show_address_list",
} as const;

const NEWS_TOOLS = {
  OPEN_NEWS: "open_news",
  CLOSE_NEWS: "close_news",
  PAUSE_NEWS: "pause_news",
  RESUME_NEWS: "resume_news",
} as const;

// Type guard functions
const isMapTool = (
  tool: string | undefined
): tool is (typeof MAP_TOOLS)[keyof typeof MAP_TOOLS] => {
  return tool !== undefined && Object.values(MAP_TOOLS).includes(tool as any);
};

const isNewsTool = (
  tool: string | undefined
): tool is (typeof NEWS_TOOLS)[keyof typeof NEWS_TOOLS] => {
  return tool !== undefined && Object.values(NEWS_TOOLS).includes(tool as any);
};

const Container = styled.div`
  width: 100%;
  height: 100dvh;
  background: ${COLORS.backgroundGradient};
  padding: ${SPACING.md};
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md};
  overflow: hidden;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    padding: ${SPACING.xs};
    gap: ${SPACING.xs};
  }
`;

const MainLayout = styled.div`
  display: flex;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  gap: ${SPACING.lg};
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    flex-direction: column;
    gap: ${SPACING.md};
  }
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.md};
  padding: ${SPACING.md} 0;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    padding: 0;
    gap: ${SPACING.sm};
    display: none;
  }
`;

const RightSection = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md};
  min-height: 0;
  overflow: hidden;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    flex: 1;
    gap: 0;
    background: ${COLORS.backgroundGradient};
    border-radius: 0;
    padding: 0;
    justify-content: space-between;
  }
`;

const OrbContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isMapVisibleOnMobile",
})<{ isMapVisibleOnMobile?: boolean }>`
  width: 360px;
  height: 360px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  @media (max-width: 768px) {
    display: none;
  }

  &::before {
    content: "";
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border-radius: 50%;
    background: ${COLORS.gradient};
    opacity: 0.3;
    filter: blur(20px);
    z-index: -1;
  }
`;

const ToolsSection = styled.div`
  border-radius: 16px;
  padding: ${SPACING.md};
  backdrop-filter: blur(10px);

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    padding: 20px ${SPACING.sm};
    border-radius: 12px;
  }
`;

const ToolsTitle = styled.div`
  color: ${COLORS.textPrimary};
  font-size: 36px;
  font-weight: 500;
  margin: 0 0 100px 0;
  text-align: center;
  text-shadow: ${SHADOWS.text};

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: 24px;
    margin: 0 0 40px 0;
  }
`;

const AgentTitle = styled.div`
  color: ${COLORS.textPrimary};
  font-size: 36px;
  font-weight: 500;
  margin: -46px 0 20px 0;
  text-align: center;
  text-transform: uppercase;
  position: relative;
  z-index: 9;
  text-shadow: ${SHADOWS.text};

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: 20px;
    margin: -30px 0 ${SPACING.sm} 0;
  }
`;

const ToolsGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 36px;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    gap: ${SPACING.sm};
    flex-direction: column;
  }
`;

const ToolCard = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "color" && prop !== "borderColor",
})<{ color: string; borderColor: string }>`
  flex: 1;
  padding: 20px;
  border-radius: 12px;
  cursor: pointer;
  transition: scale 0.3s ease;
  border: 2px solid transparent;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  max-width: 300px;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    padding: ${SPACING.sm};
    gap: 12px;
    max-width: none;
    background: rgba(251, 191, 36, 0.15);
    border-color: ${(props) => props.borderColor};
    flex-direction: initial;
    justify-content: initial;

    .anticon {
      width: 100%;
      max-width: 60px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  &:hover {
    background: rgba(251, 191, 36, 0.15);
    scale: 1.1;
    border-color: ${(props) => props.borderColor};
  }

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    &:active {
      background: rgba(251, 191, 36, 0.2);
      scale: 0.95;
      border-color: ${(props) => props.borderColor};
    }
  }

  .anticon {
    font-size: 48px;
    color: ${(props) => props.color};

    @media (max-width: ${BREAKPOINT_MOBILE}) {
      font-size: 36px;
    }
  }
`;

const ToolCardBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.sm};

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    align-items: initial;
    gap: 4px;
  }
`;

const ToolName = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "color",
})<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 18px;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: 16px;
  }
`;

const ToolDescription = styled.div`
  color: ${COLORS.gray};
  font-size: 14px;
  text-align: center;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: 12px;
  }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  margin-top: ${SPACING.sm};
  position: relative;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100dvh;
    margin: 0;
    z-index: 1000;
  }
`;

const TtsDisplayContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isMobileMapView",
})<{ isMobileMapView?: boolean }>`
  width: 100%;
  max-width: 600px;
  min-height: 120px;
  margin: 0 auto;
  padding: ${SPACING.sm};
  backdrop-filter: blur(10px);
  color: ${COLORS.textPrimary};
  font-size: 14px;
  line-height: 1.6;
  position: relative;
  text-align: center;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    max-height: 20px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: auto;
    padding: 0;

    ${(props) =>
      props.isMobileMapView
        ? `
      justify-content: flex-start;
      max-height: initial;
      text-align: initial;
      flex: 1;
      overflow: hidden;
      height: 100%;
      margin-right: -2px;
      border-radius: 30px;
    `
        : ""}
  }
`;

const TtsContent = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isMobileMapView",
})<{ isMobileMapView?: boolean }>`
  color: ${COLORS.textPrimary};
  white-space: pre-wrap;
  word-wrap: break-word;
  text-align: center;
  font-size: 18px;

  .cursor {
    display: inline-block;
    width: 2px;
    height: 1.2em;
    background: ${COLORS.primary};
    animation: blink 1s infinite;
    margin-left: 2px;
  }

  @keyframes blink {
    0%,
    50% {
      opacity: 1;
    }
    51%,
    100% {
      opacity: 0;
    }
  }

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    font-size: 14px;
    line-height: 1.4;
    width: 100%;
    display: block;

    ${(props) =>
      props.isMobileMapView
        ? `
          text-align: left;
          }
        `
        : ""}
  }
`;

const ThinkingDots = styled.span`
  display: inline-block;

  .dot {
    display: inline-block;
    animation: wave 1.4s ease-in-out infinite;
    margin-left: 2px;
  }

  .dot:nth-child(1) {
    animation-delay: 0s;
  }

  .dot:nth-child(2) {
    animation-delay: 0.2s;
  }

  .dot:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes wave {
    0%,
    60%,
    100% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-10px);
    }
  }
`;

const ThinkingText = styled.span`
  display: inline-flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.5);
  font-style: italic;
`;

const MobileMapBottomBar = styled.div<{ isDisplaying?: boolean }>`
  position: fixed;
  bottom: 10px;
  left: 10px;
  width: 60px;
  height: 60px;
  z-index: 9999;
  background: ${COLORS.cardBackground};
  border: 1px solid ${COLORS.borderColor};
  box-shadow: ${SHADOWS.card};
  border-radius: 38px;
  display: flex;
  align-items: center;
  transition: all 0.6s;

  ${(props) =>
    props.isDisplaying
      ? `
      width: calc(100% - 20px);
      gap: ${SPACING.sm};
      height: 76px;
      padding: ${SPACING.xs};
    }
  `
      : ""}
`;

const MobileOrbWrapper = styled.div`
  width: 60px;
  height: 60px;
  min-width: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

// Mini orb for inside ControlsCard on mobile
// Using peachy-pink color: #f9a8a8 (light coral/salmon)
const MiniOrbContainer = styled.div`
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const MiniOrbRing = styled.div.withConfig({
  shouldForwardProp: (prop) => !['size', 'delay', 'isActive', 'audioLevel'].includes(prop),
})<{ size: number; delay: number; isActive: boolean; audioLevel: number }>`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  background: ${props => {
    // Layer 1 (center): darkest, Layer 2: lighter, Layer 3 (outer): very faded
    // delay=0 is outer (very faded), delay=1 is middle (lighter)
    const baseOpacity = props.delay === 0 ? 0.08 : 0.2;
    if (props.isActive && props.audioLevel > 0) {
      const extraOpacity = props.audioLevel * 0.2;
      return `rgba(251, 146, 146, ${baseOpacity + extraOpacity})`;
    }
    return `rgba(249, 168, 168, ${baseOpacity})`;
  }};
  transition: transform 0.1s ease-out, background 0.1s ease-out;
  transform: ${props => {
    if (props.isActive && props.audioLevel > 0) {
      const scale = props.delay === 0
        ? 1 + (props.audioLevel * 0.5)
        : 1 + (props.audioLevel * 0.35);
      return `scale(${scale})`;
    }
    return 'scale(1)';
  }};
`;

const MiniOrbCenter = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isActive', 'audioLevel'].includes(prop),
})<{ isActive: boolean; audioLevel: number }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f9a8a8;
  z-index: 1;
  transition: box-shadow 0.1s ease-out, transform 0.1s ease-out;
  box-shadow: ${props => {
    if (props.isActive && props.audioLevel > 0) {
      const glowSize = 6 + (props.audioLevel * 10);
      return `0 0 ${glowSize}px rgba(249, 168, 168, ${0.5 + props.audioLevel * 0.3})`;
    }
    return props.isActive ? '0 0 6px rgba(249, 168, 168, 0.4)' : 'none';
  }};
  transform: ${props => props.isActive && props.audioLevel > 0 ? `scale(${1 + props.audioLevel * 0.15})` : 'scale(1)'};
`;

const MobileBottomOrbContainer = styled.div`
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const MobileBottomOrb = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(217, 119, 6, 0.3);
  box-shadow: 0 0 15px rgba(217, 119, 6, 0.15);
`;

const OrbImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const OrbVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

// New Orb Design with 3 Concentric Rings
const OrbWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const Ring = styled.div.withConfig({
  shouldForwardProp: (prop) => !['size', 'delay', 'isActive', 'audioLevel'].includes(prop),
})<{ size: number; delay: number; isActive: boolean; audioLevel: number }>`
  position: absolute;
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
  border-radius: 50%;
  border: none;
  /* delay: 0=outer (mờ nhất, z-index thấp), 1=middle (đậm hơn, z-index cao hơn) */
  z-index: ${(props) => props.delay};
  /* Giữ nguyên màu sắc khi speak - không thay đổi opacity */
  background: ${(props) => {
    const baseOpacity = props.delay === 1 ? 0.25 : 0.12;
    return `rgba(249, 168, 168, ${baseOpacity})`;
  }};
  transition: transform 0.1s ease-out;
  transform: ${(props) => {
    if (props.isActive && props.audioLevel > 0) {
      // Gap từ 16px -> 48px (gấp 3), orb 80 đứng yên
      // Middle 112 -> 176, Outer 144 -> 272
      const scale = props.delay === 0
        ? 1 + (props.audioLevel * 0.889)  // outer: 144 -> 272
        : 1 + (props.audioLevel * 0.571); // middle: 112 -> 176
      return `scale(${scale})`;
    }
    return 'scale(1)';
  }};

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    width: ${(props) => props.size * 0.67}px;
    height: ${(props) => props.size * 0.67}px;
  }

  @keyframes ring-pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.05);
      opacity: 1;
    }
  }

  ${(props) =>
    props.isActive && props.audioLevel === 0 &&
    `
    animation: ring-pulse ${1.5 + props.delay * 0.5}s ease-in-out infinite;
    animation-delay: ${props.delay * 0.2}s;
  `}
`;

const PulsingOrb = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isActive', 'audioLevel'].includes(prop),
})<{ isActive: boolean; audioLevel: number }>`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 99999px;
  opacity: 1;
  z-index: 10;
  transition: all 0.1s ease-out;
  /* Orb trong cùng đứng yên, không scale theo audio */

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    width: 54px;
    height: 54px;
  }
`;

const OrbInner = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isActive', 'audioLevel'].includes(prop),
})<{ isActive: boolean; audioLevel: number }>`
  position: relative;
  z-index: 10;
  display: grid;
  width: 80px;
  height: 80px;
  place-content: center;
  border-radius: 50%;
  background: #f9a8a8;
  /* Orb trong cùng đứng yên, không scale theo audio */

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    width: 54px;
    height: 54px;
  }
`;

const ControlsContainer = styled.div`
  width: 100%;
  max-width: 450px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin-top: 24px;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    max-width: 100%;
    margin-top: 16px;
  }
`;

const InlineReportCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;
`;

const MobileControlsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const InlineReportTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`;

const ControlsCard = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 16px;
  border-radius: 9999px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    gap: 8px;
    padding: 8px 12px;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    background: #fff;
  }
`;

const SpeakerInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 8px;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    padding-left: 4px;
  }
`;

const SpeakerNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SpeakerName = styled.span`
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  color: #1a1a1a;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: 14px;
  }
`;

const Timer = styled.span`
  font-family: "Courier New", monospace;
  font-size: 14px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.5);

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: 12px;
  }
`;

const CallId = styled.span`
  font-family: "Courier New", monospace;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: #4b5563;
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 8px;
  border-radius: 6px;
  align-self: flex-start;
  margin-top: 4px;
  word-break: break-all;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: 11px;
    padding: 2px 6px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NewConversationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 9999px;
  background: #000;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;

  &:hover {
    background: #333;
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    padding: 8px 16px;
    font-size: 13px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const ReportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 9999px;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111;
`;

const ModalSessionId = styled.div`
  font-family: "Courier New", monospace;
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 6px 10px;
  border-radius: 6px;
  word-break: break-all;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: rgb(187, 171, 237);
    box-shadow: 0 0 0 3px rgba(187, 171, 237, 0.2);
  }
`;

const ModalButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ModalButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'primary',
})<{ primary?: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${(p) => (p.primary ? "transparent" : "#d1d5db")};
  background: ${(p) => (p.primary ? "#000" : "#fff")};
  color: ${(p) => (p.primary ? "#fff" : "#374151")};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${(p) => (p.primary ? "#333" : "#f3f4f6")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalStatus = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind: "success" | "error" }>`
  font-size: 13px;
  color: ${(p) => (p.kind === "success" ? "#059669" : "#dc2626")};
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: rgb(187, 171, 237);
    box-shadow: 0 0 0 3px rgba(187, 171, 237, 0.2);
  }
`;

const ModalDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
`;

const GuidelineList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GuidelineItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 3px solid rgb(187, 171, 237);
`;

const GuidelineItemTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #111;
`;

const GuidelineItemDesc = styled.div`
  font-size: 13px;
  color: #4b5563;
`;

const UseCaseSelect = styled.select`
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  width: 100%;
  max-width: 480px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  color: rgba(0, 0, 0, 0.85);
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: #1a1a1a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UseCaseHint = styled.p.withConfig({
  shouldForwardProp: (prop) => prop !== 'isError',
})<{ isError?: boolean }>`
  margin: 4px 0 0 0;
  font-size: 12px;
  color: ${(p) => (p.isError ? "#dc2626" : "rgba(0, 0, 0, 0.5)")};
`;

const UseCaseDetailPanel = styled.div`
  width: 100%;
  max-width: 480px;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: rgba(255, 255, 255, 0.7);
  padding: 14px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  max-height: 320px;
  overflow-y: auto;
`;

const InlineUseCaseDetail = styled.div`
  width: 100%;
  display: contents;
`;

const UseCaseGuideMarkdown = styled.div`
  font-size: 13px;
  color: #1a1a1a;
  line-height: 1.55;

  h1, h2, h3, h4, h5, h6 {
    margin: 12px 0 6px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
  }
  h1 { font-size: 16px; }
  h2 { font-size: 15px; }
  h3, h4, h5, h6 { font-size: 14px; }

  p {
    margin: 6px 0;
  }

  ul, ol {
    margin: 6px 0;
    padding-left: 20px;
  }

  li {
    margin: 2px 0;
  }

  code {
    background: rgba(0, 0, 0, 0.06);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 12px;
  }

  pre {
    background: rgba(0, 0, 0, 0.05);
    padding: 8px 10px;
    border-radius: 6px;
    overflow-x: auto;
    font-size: 12px;
  }

  blockquote {
    margin: 6px 0;
    padding: 4px 10px;
    border-left: 3px solid rgb(187, 171, 237);
    color: #4b5563;
    background: rgba(187, 171, 237, 0.08);
    border-radius: 0 6px 6px 0;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 8px 0;
    font-size: 12px;
  }

  th, td {
    border: 1px solid rgba(0, 0, 0, 0.1);
    padding: 4px 8px;
    text-align: left;
  }

  th {
    background: rgba(0, 0, 0, 0.04);
    font-weight: 600;
  }

  a {
    color: #2563eb;
    text-decoration: underline;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;

const RefreshButton = styled.button`
  margin-left: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UseCaseRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 480px;
`;

const TesterBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  background: rgba(187, 171, 237, 0.2);
  border: 1px solid rgba(187, 171, 237, 0.4);
  border-radius: 9999px;
  cursor: pointer;
  align-self: flex-start;
  margin-top: 4px;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(187, 171, 237, 0.3);
  }
`;

const ControlButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: "primary" | "danger" }>`
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 50%;
  background: ${(props) =>
    props.variant === "danger"
      ? "rgb(248, 113, 113)"
      : "rgba(0, 0, 0, 0.08)"};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${(props) => (props.variant === "danger" ? "#fff" : "#1a1a1a")};

  &:hover {
    background: ${(props) =>
      props.variant === "danger"
        ? "rgb(239, 68, 68)"
        : "rgba(0, 0, 0, 0.12)"};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    width: 36px;
    height: 36px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const FullScreenInitial = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${COLORS.backgroundGradient};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 40px 20px;
  z-index: 10000;
  overflow-y: auto;

  @media (max-width: 500px) {
    padding: 24px 16px;
    justify-content: space-between;
    overflow-y: hidden;
  }
`;

const InitialTitle = styled.h1`
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 40px 0 12px 0;
  text-align: center;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-top: 32px;
  }

  @media (max-width: 500px) {
    font-size: 20px;
    margin: 16px 0 8px 0;
  }
`;

const InitialSubtitle = styled.p`
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  margin: 0 0 24px 0;
  text-align: center;
  max-width: 560px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 20px;
  }

  @media (max-width: 500px) {
    font-size: 13px;
    margin-bottom: 16px;
    line-height: 1.5;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  gap: 16px;
  width: 100%;

  @media (max-width: 500px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`;

const MobileCardWrapper = styled.div<{ index: number; isExpanded: boolean; visibleCount: number }>`
  display: flex;
  height: 100%;

  @media (max-width: 500px) {
    display: ${props => (props.index < props.visibleCount || props.isExpanded) ? 'block' : 'none'};
    height: auto;
  }
`;

const PurposeCard = styled.div`
  border-radius: 20px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  transition: all 0.2s ease;
  opacity: 0.9;
  border: 2px solid transparent;
  height: 100%;
  width: 100%;

  &:hover {
    opacity: 1;
    transform: translateY(-2px);
  }

  @media (max-width: 500px) {
    flex-direction: row;
    align-items: center;
    text-align: left;
    padding: 10px 12px;
    border-radius: 12px;
    gap: 10px;
    width: 100%;
    height: auto;
  }
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: inherit;
  }

  @media (max-width: 500px) {
    width: 32px;
    height: 32px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 500px) {
    text-align: left;
  }
`;

const CardTitle = styled.h3`
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 6px 0;

  @media (max-width: 500px) {
    font-size: 12px;
    margin: 0 0 2px 0;
  }
`;

const CardDescription = styled.p`
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
  margin: 0;
  line-height: 1.5;

  @media (max-width: 500px) {
    font-size: 11px;
    line-height: 1.3;
  }
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 20px;
  max-width: 500px;
  width: 100%;
  margin-bottom: 24px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
`;

const InfoCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;

  svg {
    width: 18px;
    height: 18px;
    color: #c49100;
  }
`;

const InfoCardText = styled.p`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
  margin: 0;
  line-height: 1.6;
`;

const LanguageSelectorContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 650px;

  @media (max-width: 500px) {
    margin-bottom: 16px;
    gap: 8px;
  }
`;

const LanguageLabel = styled.p`
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.5);
  margin: 0;
`;

const LanguagePillRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LanguagePillFlag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  font-size: 20px;
  line-height: 1;
`;

const LanguagePillChange = styled.button`
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 1);
    border-color: rgba(0, 0, 0, 0.25);
  }
`;

const ScenarioSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 650px;
  margin-bottom: 24px;

  @media (max-width: 500px) {
    margin-bottom: 16px;
    gap: 8px;
  }
`;

const ScenarioLabel = styled.p`
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.5);
  margin: 0;
`;

const ScenarioButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 500px) {
    gap: 8px;
    width: 100%;
  }
`;

const ScenarioButton = styled.button<{ isActive: boolean }>`
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 12px;
  border: 2px solid ${props => props.isActive ? '#1a1a1a' : 'rgba(0, 0, 0, 0.1)'};
  background: ${props => props.isActive ? '#1a1a1a' : 'rgba(255, 255, 255, 0.8)'};
  color: ${props => props.isActive ? '#fff' : 'rgba(0, 0, 0, 0.7)'};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    border-color: ${props => props.isActive ? '#1a1a1a' : 'rgba(0, 0, 0, 0.3)'};
    background: ${props => props.isActive ? '#2a2a2a' : 'rgba(255, 255, 255, 1)'};
  }

  .anticon {
    font-size: 16px;
  }

  @media (max-width: 500px) {
    padding: 8px 14px;
    font-size: 13px;
    flex: 1;
    justify-content: center;
    min-width: 0;

    .anticon {
      font-size: 14px;
    }
  }
`;

const CardsWrapper = styled.div<{ hasHiddenCards?: boolean }>`
  position: relative;
  background: #ffffff;
  border-radius: 28px;
  padding: 24px;
  max-width: 720px;
  width: 100%;
  margin-bottom: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);

  @media (max-width: 500px) {
    padding: 12px;
    padding-bottom: ${props => (props.hasHiddenCards ? '24px' : '12px')};
    border-radius: 16px;
    max-width: none;
    width: 100%;
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

    &::after {
      content: ${props => (props.hasHiddenCards ? "''" : 'none')};
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 40px;
      background: linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%);
      border-radius: 0 0 16px 16px;
      pointer-events: none;
    }
  }
`;

const ExpandIconButton = styled.button`
  display: none;
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: rgba(0, 0, 0, 0.25);
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px;
  z-index: 2;

  &:hover {
    color: rgba(0, 0, 0, 0.5);
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 500px) {
    display: flex;
  }
`;

const BeginButton = styled.button`
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  padding: 18px 72px;
  font-size: 17px;
  font-weight: 600;
  color: #fff;
  background: #1a1a1a;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.5px;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    background: #2a2a2a;
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    box-shadow: none;
  }

  @media (max-width: 500px) {
    width: 100%;
    padding: 14px 32px;
    font-size: 15px;
    margin-bottom: 16px;
  }
`;

const RingingScreen = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${COLORS.backgroundGradient};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  z-index: 10000;
`;

const RingingOrbWrapper = styled.div`
  position: relative;
  width: 220px;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
`;

const RingingRing = styled.div<{ size: number; delay: number }>`
  position: absolute;
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
  border-radius: 50%;
  background: ${(props) => {
    const baseOpacity = props.delay === 2 ? 0.15 : props.delay === 1 ? 0.1 : 0.05;
    return `rgba(196, 145, 0, ${baseOpacity})`;
  }};
  animation: ringing-pulse 1.5s ease-in-out infinite;
  animation-delay: ${(props) => props.delay * 0.15}s;

  @keyframes ringing-pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.12);
      opacity: 0.6;
    }
  }
`;

const RingingOrbCenter = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: #fff;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);

  svg {
    width: 36px;
    height: 36px;
    color: #1a1a1a;
    animation: phone-shake 0.4s ease-in-out infinite;
  }

  @keyframes phone-shake {
    0%, 100% {
      transform: rotate(-12deg);
    }
    50% {
      transform: rotate(12deg);
    }
  }
`;

const RingingText = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.45);
  margin: 0 0 8px 0;
`;

const CallerName = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 24px 0;
  letter-spacing: 0.3px;
`;

const CallerPhone = styled.p`
  font-size: 22px;
  font-weight: 500;
  color: #1a1a1a;
  margin: -8px 0 32px 0;
  letter-spacing: 1.5px;
`;

const AnswerButton = styled.button`
  padding: 18px 56px;
  font-size: 17px;
  font-weight: 600;
  color: #fff;
  background: #1a1a1a;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    transform: scale(1.05);
    background: #2a2a2a;
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const TopBar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isMobileMapView",
})<{ isMobileMapView?: boolean }>`
  position: fixed;
  top: ${(props) => (props.isMobileMapView ? "60px" : "0")};
  right: 0;
  height: 40px;
  display: flex;
  align-items: center;
  z-index: 10000;
  color: ${COLORS.gray};
  font-size: 12px;
  padding: 4px;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    height: 32px;
  }
`;

const InfoIcon = styled(InfoCircleOutlined)`
  color: ${COLORS.gray};
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: ${COLORS.primary};
    transform: scale(1.1);
  }

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: 16px;
  }
`;

const ShareButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.gray};
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${COLORS.primary};
    border-color: ${COLORS.borderColor};
    background: rgba(255, 255, 255, 1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .anticon {
    font-size: 14px;
  }
`;

const InitialShareButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 1);
    border-color: rgba(0, 0, 0, 0.25);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .anticon {
    font-size: 14px;
  }

  @media (max-width: 500px) {
    padding: 8px 16px;
    font-size: 12px;
  }
`;

const StatusContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
  min-width: 200px;
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${SPACING.sm};
`;

const StatusLabel = styled.span`
  color: ${COLORS.gray};
  font-size: 12px;
  font-weight: 500;
`;

const StatusValue = styled.span<{ isConnected?: boolean }>`
  color: ${(props) => (props.isConnected ? "#52c41a" : "#ff4d4f")};
  font-size: 12px;
  font-family: monospace;
  font-weight: 600;
`;

const ClientIdValue = styled.span`
  color: ${COLORS.textPrimary};
  font-size: 12px;
  font-family: monospace;
  user-select: all;
  word-break: break-all;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${COLORS.backgroundGradient};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.md};
  z-index: 10000;
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid rgba(217, 119, 6, 0.2);
  border-top-color: ${COLORS.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    width: 40px;
    height: 40px;
    border-width: 3px;
  }
`;

const LoadingText = styled.div`
  color: ${COLORS.textPrimary};
  font-size: 18px;
  text-align: center;
  text-shadow: ${SHADOWS.text};

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: 16px;
  }
`;

const ConversationContainer = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  background: ${COLORS.cardBackground};
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: ${SPACING.md};
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
  overflow: hidden;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    padding: 0;
    border-radius: 0;
    border: none;
    background: transparent;
    gap: 4px;
  }
`;

const ConversationTitle = styled.div`
  color: ${COLORS.primary};
  font-size: 18px;
  font-weight: 600;
  padding-bottom: 12px;
  border-bottom: 1px solid ${COLORS.borderColor};
`;

const ConversationMessages = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding-right: 8px;
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }

  @media (max-width: 768px) {
    gap: 12px;
    padding: 10px;

    &::-webkit-scrollbar {
      width: 3px;
    }
  }
`;

const GuidePane = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }

  @media (max-width: 768px) {
    padding: 10px;

    &::-webkit-scrollbar {
      width: 3px;
    }
  }
`;

const GuideEmpty = styled.div`
  color: #9ca3af;
  font-size: 13px;
  text-align: center;
  padding: 24px 12px;
`;

const MessageBubble = styled.div<{ role: string; isPending?: boolean }>`
  padding: 12px 16px;
  border-radius: 12px;
  max-width: 85%;
  word-wrap: break-word;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.5;
  color: ${(props) => props.isPending ? '#9ca3af' : '#1f2937'};
  ${(props) => props.isPending && `
    font-style: italic;
    animation: pulse 1.5s ease-in-out infinite;
  `}

  @keyframes pulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }

  ${(props) =>
    props.role === "user"
      ? `
    align-self: flex-end;
    background: #eef2ff;
    border: 1px solid #c7d2fe;
    border-bottom-right-radius: 4px;
  `
      : props.role === "assistant"
      ? `
    align-self: flex-start;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-bottom-left-radius: 4px;
  `
      : `
    display: none;
  `}

  ${(props) => props.isPending && `
    background: #f3f4f6 !important;
    border-color: #d1d5db !important;
  `}

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
    border-radius: 10px;
    max-width: 90%;
  }
`;

const MessageRoleWrapper = styled.div<{ role: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid ${(props) => props.role === "user" ? "rgba(99, 102, 241, 0.12)" : "rgba(34, 197, 94, 0.12)"};
`;

const MessageRoleIcon = styled.span<{ role: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 10px;
  background: ${(props) => props.role === "user" ? "#e0e7ff" : "#dcfce7"};
  color: ${(props) => props.role === "user" ? "#4f46e5" : "#16a34a"};
`;

const MessageRole = styled.span<{ role: string }>`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${(props) => (props.role === "user" ? "#4f46e5" : "#16a34a")};
`;

const MessageRoleBadge = styled.span<{ role: string }>`
  font-size: 9px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 4px;
  margin-left: auto;
  background: ${(props) => props.role === "user" ? "#e0e7ff" : "#dcfce7"};
  color: ${(props) => props.role === "user" ? "#4f46e5" : "#16a34a"};
`;

const EmptyConversation = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
  text-align: center;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  padding: 20px;
  background: #fff;
`;

// Tab Components
const TabsContainer = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 0;
  padding: 4px;
  background: #f3f4f6;
  border-radius: 10px;
`;

const Tab = styled.button<{ active: boolean }>`
  position: relative;
  padding: 10px 18px;
  background: ${props => props.active ? '#fff' : 'transparent'};
  border: none;
  border-radius: 8px;
  color: ${props => props.active ? '#111827' : '#6b7280'};
  font-size: 13px;
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};

  &:hover {
    color: #111827;
  }

  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
    padding: 8px 12px;
    font-size: 11px;
    gap: 4px;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

// Survey Report Components - Professional Table Layout
const ReportContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 12px;
  gap: 16px;
  background: #fff;
  border-radius: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

const ReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const ReportTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReportTitleText = styled.span`
  color: #111827;
  font-weight: 700;
  font-size: 16px;
  letter-spacing: -0.025em;
`;

const ReportSubtitle = styled.span`
  color: #6b7280;
  font-size: 12px;
`;

const ReportStatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const ReportStatus = styled.span<{ isComplete: boolean }>`
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.025em;
  background: ${props => props.isComplete ? '#dcfce7' : '#fef3c7'};
  color: ${props => props.isComplete ? '#166534' : '#92400e'};
  border: 1px solid ${props => props.isComplete ? '#bbf7d0' : '#fde68a'};
`;

const CompletionBar = styled.div<{ percentage: number }>`
  width: 100px;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    width: ${props => props.percentage}%;
    height: 100%;
    background: ${props => props.percentage === 100
      ? 'linear-gradient(90deg, #22c55e 0%, #4ade80 100%)'
      : 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)'};
    border-radius: 2px;
    transition: width 0.5s ease;
  }
`;

const ReportTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const ReportThead = styled.thead`
  background: #f9fafb;

  th {
    padding: 12px 14px;
    text-align: left;
    color: #6b7280;
    font-weight: 500;
    font-size: 12px;
    border-bottom: 1px solid #e5e7eb;

    &:first-child {
      border-top-left-radius: 7px;
    }

    &:last-child {
      border-top-right-radius: 7px;
    }
  }
`;

const ReportTbody = styled.tbody`
  tr {
    transition: background 0.15s ease;

    &:not(:last-child) {
      border-bottom: 1px solid #f3f4f6;
    }

    &:last-child {
      td:first-child {
        border-bottom-left-radius: 7px;
      }

      td:last-child {
        border-bottom-right-radius: 7px;
      }
    }

    &:hover {
      background: #fafafa;
    }
  }

  td {
    padding: 12px 14px;
    color: #374151;
    vertical-align: middle;
    font-size: 13px;
  }
`;

const QuestionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const QuestionText = styled.span`
  color: #374151;
  font-weight: 500;
`;

const AnswerCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AnswerValue = styled.span`
  color: #111827;
  font-weight: 500;
`;

const AnswerSubtext = styled.span`
  color: #6b7280;
  font-size: 11px;
`;

const ReportBadge = styled.span<{ type: 'answered' | 'skipped' | 'pending' }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  background: ${props =>
    props.type === 'answered' ? '#f0fdf4' :
    props.type === 'skipped' ? '#f9fafb' :
    '#fffbeb'};
  color: ${props =>
    props.type === 'answered' ? '#15803d' :
    props.type === 'skipped' ? '#6b7280' :
    '#b45309'};
  border: 1px solid ${props =>
    props.type === 'answered' ? '#d1fae5' :
    props.type === 'skipped' ? '#e5e7eb' :
    '#fef3c7'};
`;

const ScoreCell = styled.div<{ score?: number }>`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ScoreValue = styled.span<{ score?: number }>`
  font-weight: 700;
  font-size: 18px;
  color: ${props =>
    props.score && props.score >= 5 ? '#16a34a' :
    props.score && props.score >= 3 ? '#d97706' :
    props.score ? '#dc2626' :
    '#d1d5db'};
`;

const ScoreBar = styled.div<{ score?: number }>`
  flex: 1;
  max-width: 80px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    width: ${props => props.score ? (props.score / 7) * 100 : 0}%;
    height: 100%;
    background: ${props =>
      props.score && props.score >= 5 ? 'linear-gradient(90deg, #22c55e 0%, #4ade80 100%)' :
      props.score && props.score >= 3 ? 'linear-gradient(90deg, #f59e0b 0%, #fcd34d 100%)' :
      'linear-gradient(90deg, #ef4444 0%, #f87171 100%)'};
    border-radius: 3px;
    transition: width 0.3s ease;
  }
`;

const ScoreLabel = styled.span`
  font-size: 12px;
  color: rgba(148, 163, 184, 0.7);
`;

const EmptyReport = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 14px;
  text-align: center;
  flex-direction: column;
  gap: 12px;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  padding: 40px 20px;

  @media (max-width: 768px) {
    flex: none;
    padding: 24px 16px;
    margin-top: 12px;
  }
`;

const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border-radius: 16px;
  font-size: 28px;
`;

const MissingQuestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const MissingTag = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.2);
`;

const CallCenter = () => {
  // ---------- MOBILE DETECTION ----------
  const isMobile = useIsMobile();

  // ---------- INTERNATIONALIZATION ----------
  const { i18n } = useTranslation();

  // Shared link inherits the sharer's language: when ?lang=<vi|km|en> is
  // present, override i18n on mount so the visitor sees the same language
  // the sharer was using when they generated the link.
  useEffect(() => {
    const sharedLang = readLangParam();
    if (!sharedLang) return;
    if (i18n.language !== sharedLang) {
      void i18n.changeLanguage(sharedLang);
    }
    try {
      localStorage.setItem(LANGUAGE_CHOSEN_KEY, "1");
    } catch {
      // ignore storage failures (private mode, quota)
    }
    setHasChosenLanguage(true);
    // Run once on mount; URL params don't change without a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- MAIN STATE ----------
  const [notificationState, setNotificationState] = useState({
    title: "",
    message: "",
  });
  const [isStart, setIsStart] = useState<boolean>(false);
  const isStartRef = useRef<boolean>(false);
  const [callDuration, setCallDuration] = useState<number>(0);
  // ── Phone-simulation settings (gear icon → Drawer) ────────────────
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [phoneSimMode, setPhoneSimMode] = useState<FilterMode>("off");
  const [phoneSimConfig, setPhoneSimConfig] = useState<FilterConfig>(() =>
    profileToConfig("off")
  );
  const [phoneSimInputRate, setPhoneSimInputRate] = useState<number>(16000);
  // Per-call Khmer STT model — one of STT_MODEL_OPTIONS ("v4" default, "v1",
  // "v5"). Persisted across reloads; sent as WS `stt_model`.
  const [sttModel, setSttModelState] = useState<string>(() => readStoredSttModel());
  const setSttModel = useCallback((m: string) => {
    setSttModelState(normalizeSttModel(m));
    writeStoredSttModel(m);
  }, []);
  const [isMicMuted, setIsMicMuted] = useState<boolean>(false);
  const [callPhase, setCallPhase] = useState<'initial' | 'ringing' | 'active' | 'ended'>('initial');
  // Mirror callPhase for closures (timers, callbacks) that need a stable read
  const callPhaseRef = useRef<typeof callPhase>(callPhase);
  useEffect(() => { callPhaseRef.current = callPhase; }, [callPhase]);
  const [isCardsExpanded, setIsCardsExpanded] = useState<boolean>(false);
  const [visibleCardCount, setVisibleCardCount] = useState<number>(2);
  // Initial scenario:
  //   1. ?scenario=<valid> from URL → use it
  //   2. shared visitor without scenario → randomize across all scenarios
  //   3. fresh user → default ha_scenario
  const [selectedScenario, setSelectedScenario] = useState<string>(() => {
    const fromUrl = readScenarioParam();
    if (fromUrl) return fromUrl;
    if (readSharedByParam()) return pickRandom(Array.from(VALID_SCENARIOS));
    return "ha_scenario";
  });
  const [hasChosenLanguage, setHasChosenLanguage] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LANGUAGE_CHOSEN_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [isEditingLanguage, setIsEditingLanguage] = useState<boolean>(false);
  const callMode = "ai" as const;
  const ringtoneRef = useRef<HTMLAudioElement | null>(null);
  const [selectedTool, setSelectedTool] = useState<string>("home");
  const selectedToolRef = useRef<string>("home");

  const [currentNewsTitle, setCurrentNewsTitle] = useState<string>("");
  const [newsDuration, setNewsDuration] = useState<number>(0);
  const [fullContent, setFullContent] = useState<string>("");
  const newsContentElementRef = useRef<HTMLDivElement>(null); // Ref for news content element
  const newsCharIndexRef = useRef<number>(0);
  const isCharacterDisplayingRef = useRef<boolean>(false);
  const newsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastFullContentRef = useRef<string>(""); // Track last fullContent to detect changes
  const [locationChoices, setLocationChoices] = useState<
    Array<{ ref_id: string; display: string }>
  >([]);

  // TTS Content Display State
  const ttsContentElementRef = useRef<HTMLSpanElement>(null);
  const ttsMobileContentElementRef = useRef<HTMLSpanElement>(null); // Separate ref for mobile
  const ttsFullContentRef = useRef<string>("");
  const ttsCharIndexRef = useRef<number>(0);
  const isTtsCharacterDisplayingRef = useRef<boolean>(false);
  const hasMarkedCompleteRef = useRef<boolean>(false);
  const ttsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const displayedContentIdRef = useRef<string>(""); // Track which content is currently displayed
  const markCurrentContentCompleteRef = useRef<() => void>(() => {}); // Ref to markComplete function
  const [isTtsDisplaying, setIsTtsDisplaying] = useState<boolean>(false);
  const [isTtsCharacterDisplaying, setIsTtsCharacterDisplaying] =
    useState<boolean>(false);
  // Keep ttsFullContent as state only for triggering useEffect when content changes
  const [ttsFullContent, setTtsFullContent] = useState<string>("");
  const [isNewsPlayingOnMap, setIsNewsPlayingOnMap] = useState<boolean>(false);
  const [isThinking, setIsThinking] = useState<boolean>(false);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);

  // Audio level state for orb animation
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const audioAnimationRef = useRef<NodeJS.Timeout | null>(null);

  // Conversation history state
  const [conversationHistory, setConversationHistory] = useState<Array<{role: string, content: string, isPending?: boolean}>>([]);
  const conversationMessagesRef = useRef<HTMLDivElement>(null);
  // Track if there's a pending user message waiting to be filled by transcription
  const hasPendingUserMessageRef = useRef<boolean>(false);

  // Survey Report state
  const [activeTab, setActiveTab] = useState<'guide' | 'conversation' | 'report'>('guide');
  const [surveyReport, setSurveyReport] = useState<any>(null);
  const [isWaitingForSurveyReport, setIsWaitingForSurveyReport] = useState<boolean>(false);
  const isWaitingForSurveyReportRef = useRef<boolean>(false);
  const surveyReportTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Backend session ID (received from session.created event)
  const backendSessionIdRef = useRef<string | null>(null);
  const [displayedCallId, setDisplayedCallId] = useState<string | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState<boolean>(false);
  const [reportText, setReportText] = useState<string>("");
  const [reportSubmitting, setReportSubmitting] = useState<boolean>(false);
  const [reportStatus, setReportStatus] = useState<{ kind: "success" | "error"; msg: string } | null>(null);
  // Shared-link mode: when the URL contains ?shared_by=<name>, the visitor
  // arrived from a shared link. They skip the name-entry modal and the
  // scenario/use-case selectors, and answering the call is collapsed into a
  // single click on the Begin button.
  const sharedByName = useMemo(() => readSharedByParam(), []);
  const isShared = !!sharedByName;
  const sharedLangFromUrl = useMemo(() => readLangParam(), []);
  // Shared visitors get the auto-call zero-click flow only on their first call.
  // After the first call ends, subsequent calls behave like a regular pick-and-
  // begin flow (use-case picker shown, Begin no longer auto-fires) but the
  // home-page decorations stay hidden so they don't bounce back to "trang chủ".
  const [sharedFirstCallDone, setSharedFirstCallDone] = useState<boolean>(false);
  const sharedAutoFlow = isShared && !sharedFirstCallDone;
  const [testerName, setTesterName] = useState<string>(() => {
    if (sharedByName) return `shared_${sharedByName}`;
    try { return localStorage.getItem(TESTER_NAME_KEY) || ""; } catch { return ""; }
  });
  const [testerNameModalOpen, setTesterNameModalOpen] = useState<boolean>(false);
  const [testerNameInput, setTesterNameInput] = useState<string>("");
  const [guidelineModalOpen, setGuidelineModalOpen] = useState<boolean>(false);
  type UseCaseItem = { id: string; description: string; row: string[] };
  const [useCases, setUseCases] = useState<UseCaseItem[]>([]);
  const [selectedUseCaseId, setSelectedUseCaseId] = useState<string>("");
  const [useCasesLoading, setUseCasesLoading] = useState<boolean>(false);
  const [useCasesError, setUseCasesError] = useState<string>("");

  const reloadUseCases = useCallback(async (scenario: string, signal?: { cancelled: boolean }) => {
    setUseCasesLoading(true);
    setUseCasesError("");
    try {
      const backendUrl = resolveBackendUrl();
      const res = await fetch(`${backendUrl}/v1/scenarios/${scenario}/use-cases`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!signal?.cancelled) {
        setUseCases(data.use_cases || []);
      }
    } catch (err) {
      if (!signal?.cancelled) {
        // No use-case list available (endpoint 404/503/unreachable) → fall back
        // to freestyle silently instead of showing an alarming error banner.
        // The Begin button stays enabled when useCases is empty (freestyle).
        console.warn(`[use-cases] load failed, entering freestyle: ${(err as Error).message}`);
        setUseCases([]);
        setUseCasesError("");
      }
    } finally {
      if (!signal?.cancelled) setUseCasesLoading(false);
    }
  }, []);

  // Fetch use cases whenever scenario changes (always real-time from sheet)
  useEffect(() => {
    if (!selectedScenario) return;
    const signal = { cancelled: false };
    setSelectedUseCaseId("");
    reloadUseCases(selectedScenario, signal);
    return () => { signal.cancelled = true; };
  }, [selectedScenario, reloadUseCases]);

  // Shared mode (first call only): the visitor doesn't pick a use case.
  //   1. ?use_case=<id> matches a loaded item → use it (sharer pinned a test case)
  //   2. otherwise → randomize across the loaded list
  // After the first call ends we let them choose explicitly.
  useEffect(() => {
    if (!sharedAutoFlow) return;
    if (useCases.length > 0 && !selectedUseCaseId) {
      const fromUrl = readUseCaseParam();
      const pinned = fromUrl ? useCases.find((uc) => uc.id === fromUrl) : null;
      setSelectedUseCaseId(pinned ? pinned.id : pickRandom(useCases).id);
    }
  }, [sharedAutoFlow, useCases, selectedUseCaseId]);

  // Mark the shared first-call as done once it ends, so the next 'initial'
  // render shows the use-case picker instead of auto-beginning again.
  useEffect(() => {
    if (isShared && !sharedFirstCallDone && callPhase === 'ended') {
      setSharedFirstCallDone(true);
    }
  }, [isShared, sharedFirstCallDone, callPhase]);

  // Currently-selected use case object
  const selectedUseCase = useMemo(
    () => useCases.find((uc) => uc.id === selectedUseCaseId) || null,
    [useCases, selectedUseCaseId],
  );

  // Guide text lives entirely in column C of the use case row, formatted as markdown.
  const useCaseGuide = useMemo(() => {
    if (!selectedUseCase) return "";
    return (selectedUseCase.row[2] || "").trim();
  }, [selectedUseCase]);

  const handleSaveTesterName = useCallback(() => {
    const name = testerNameInput.trim();
    if (!name) return;
    try { localStorage.setItem(TESTER_NAME_KEY, name); } catch { /* ignore */ }
    setTesterName(name);
    setTesterNameModalOpen(false);
  }, [testerNameInput]);

  const handleEditTesterName = useCallback(() => {
    setTesterNameInput(testerName);
    setTesterNameModalOpen(true);
  }, [testerName]);

  // Share-link generation: builds <origin>/call-center?shared_by=<name> from
  // the tester's name and copies it to the clipboard. The receiving visitor
  // skips name entry / use-case selection and the call auto-answers.
  const handleShare = useCallback(async () => {
    console.log("[SHARE] click", { testerName, isShared });
    clientLogger.log("share_click", { has_tester_name: !!testerName });
    if (!testerName) {
      handleEditTesterName();
      return;
    }
    const baseName = testerName.startsWith("shared_") ? testerName.slice("shared_".length) : testerName;
    const url = new URL(window.location.href);
    url.search = "";
    url.hash = "";
    url.searchParams.set(SHARED_BY_PARAM, baseName);
    const sharerLang = (i18n.language || "").toLowerCase();
    if (SUPPORTED_LANGS.has(sharerLang)) {
      url.searchParams.set(LANG_PARAM, sharerLang);
    }
    // Pin the sharer's scenario + test case so the recipient runs the same
    // configuration. When the sharer hasn't picked a use case, omit both —
    // the recipient will randomize on landing.
    if (selectedUseCaseId && VALID_SCENARIOS.has(selectedScenario)) {
      url.searchParams.set(SCENARIO_PARAM, selectedScenario);
      url.searchParams.set(USE_CASE_PARAM, selectedUseCaseId);
    }
    const shareUrl = url.toString();
    const hasClipboardApi = typeof navigator !== "undefined"
      && !!navigator.clipboard
      && typeof navigator.clipboard.writeText === "function";
    const fallbackCopy = (text: string): boolean => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, text.length);
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
      } catch (e) {
        console.error("[SHARE] fallback copy failed", e);
        return false;
      }
    };
    let copied = false;
    if (hasClipboardApi) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        copied = true;
      } catch (err) {
        console.warn("[SHARE] clipboard.writeText rejected, using fallback", err);
      }
    }
    if (!copied) {
      copied = fallbackCopy(shareUrl);
    }
    clientLogger.log("share_result", { copied, has_clipboard_api: hasClipboardApi });
    if (copied) {
      setNotificationState({
        title: "Đã sao chép link",
        message: "Link chia sẻ đã được sao chép vào clipboard.",
      });
    } else {
      setNotificationState({
        title: "Link chia sẻ",
        message: shareUrl,
      });
    }
  }, [testerName, isShared, i18n, handleEditTesterName, selectedScenario, selectedUseCaseId]);

  // Auto-scroll conversation to bottom when new messages are added
  useEffect(() => {
    if (conversationMessagesRef.current) {
      conversationMessagesRef.current.scrollTop = conversationMessagesRef.current.scrollHeight;
    }
  }, [conversationHistory]);

  // Dynamic card visibility calculation based on viewport height
  useEffect(() => {
    const calculateVisibleCards = () => {
      if (window.innerWidth > 500) {
        // Desktop: always show all cards in 2x2 grid
        setVisibleCardCount(4);
        return;
      }

      // Mobile layout measurements (approximate):
      // - Title: ~50px
      // - Subtitle: ~60px
      // - Card height: ~60px each (with padding/gap ~68px)
      // - Language selector: ~80px
      // - Begin button: ~70px
      // - Padding/margins: ~80px
      // - CardsWrapper padding: ~48px (top + bottom)
      // - Expand button area: ~32px

      const viewportHeight = window.innerHeight;
      const fixedContentHeight = 50 + 60 + 80 + 70 + 80 + 48 + 32; // ~420px
      const availableForCards = viewportHeight - fixedContentHeight;
      const cardHeight = 68; // card + gap

      // Calculate how many cards can fit
      const cardsCanFit = Math.floor(availableForCards / cardHeight);

      // Clamp between 2 and 4
      const visibleCount = Math.max(2, Math.min(4, cardsCanFit));
      setVisibleCardCount(visibleCount);
    };

    calculateVisibleCards();
    window.addEventListener('resize', calculateVisibleCards);

    return () => {
      window.removeEventListener('resize', calculateVisibleCards);
    };
  }, []);

  const [isConnectionComplete, setIsConnectionComplete] =
    useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);

  // Track texts waiting for audio (for TTS queue) - queue of texts in order
  const textsWaitingForAudioRef = useRef<string[]>([]);
  // Track if we've received audio for the current text (to avoid multiple marks)
  const hasReceivedAudioForCurrentTextRef = useRef<boolean>(false);

  // Track if we're waiting for audio from speak_start to finish (when deviceState is "home")
  const isWaitingForSpeakAudioRef = useRef<boolean>(false);
  // Queue for tool events that need to wait for audio to finish
  const pendingToolEventsRef = useRef<Array<{ tool: string; data: any }>>([]);
  // Track if we're currently in a speak session (between speak_start and speak_end)
  const isInSpeakSessionRef = useRef<boolean>(false);
  // Track current speaking text for event acknowledgment
  // Track if we're in streaming audio mode (receiving raw PCM chunks)
  const isStreamingAudioModeRef = useRef<boolean>(false);
  // Track if survey is finished (received is_finish flag from backend)
  const isSurveyFinishedRef = useRef<boolean>(false);
  // Track current turn_id from server (for playback.completed validation)
  const currentTurnIdRef = useRef<number | null>(null);
  // Track audio diagnostics (for verifying client actually heard the bot)
  // Reset at the start of each bot speech segment so each segment reports
  // its own delivery stats independently.
  const lastReceivedAudioSeqRef = useRef<number | null>(null);
  const audioChunksReceivedRef = useRef<number>(0);
  const audioBytesReceivedRef = useRef<number>(0);
  const firstAudioChunkAtRef = useRef<number | null>(null);
  const lastAudioChunkAtRef = useRef<number | null>(null);
  // Guard so shared-link auto-begin runs only once per page load
  const hasAutoBegunRef = useRef<boolean>(false);

  // Keep refs in sync with state
  useEffect(() => {
    isStartRef.current = isStart;
  }, [isStart]);

  const reportAutoOpenedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (callPhase !== 'ended' || !displayedCallId) return;
    if (reportAutoOpenedForRef.current === displayedCallId) return;
    reportAutoOpenedForRef.current = displayedCallId;
    setReportText("");
    setReportStatus(null);
    setReportModalOpen(true);
  }, [callPhase, displayedCallId]);

  useEffect(() => {
    if (callPhase === 'initial') {
      reportAutoOpenedForRef.current = null;
    }
  }, [callPhase]);

  // Call duration timer
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isStart) {
      setCallDuration(0);
      interval = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isStart]);

  useEffect(() => {
    selectedToolRef.current = selectedTool;
  }, [selectedTool]);

  // Simulate audio level animation when speaking
  useEffect(() => {
    if (isSpeaking) {
      // Create dynamic audio level simulation
      const animateAudioLevel = () => {
        // Generate random audio level between 0.3 and 1.0 for natural effect
        const newLevel = 0.3 + Math.random() * 0.7;
        setAudioLevel(newLevel);
      };

      // Start animation interval
      audioAnimationRef.current = setInterval(animateAudioLevel, 100);

      return () => {
        if (audioAnimationRef.current) {
          clearInterval(audioAnimationRef.current);
          audioAnimationRef.current = null;
        }
        setAudioLevel(0);
      };
    } else {
      // Reset audio level when not speaking
      setAudioLevel(0);
      if (audioAnimationRef.current) {
        clearInterval(audioAnimationRef.current);
        audioAnimationRef.current = null;
      }
    }
  }, [isSpeaking]);

  // ---------- HELPER FUNCTIONS ----------
  const resetNewsContentDisplay = useCallback(() => {
    // Clear any running interval
    if (newsIntervalRef.current) {
      clearInterval(newsIntervalRef.current);
      newsIntervalRef.current = null;
    }
    newsCharIndexRef.current = 0;
    isCharacterDisplayingRef.current = false;
    // Clear DOM directly
    if (newsContentElementRef.current) {
      newsContentElementRef.current.textContent = "";
    }
  }, []);

  const resetTtsContentDisplay = useCallback(() => {
    // Clear any running interval
    if (ttsIntervalRef.current) {
      clearInterval(ttsIntervalRef.current);
      ttsIntervalRef.current = null;
    }
    ttsCharIndexRef.current = 0;
    ttsFullContentRef.current = "";
    displayedContentIdRef.current = "";
    isTtsCharacterDisplayingRef.current = false;
    // Update DOM directly - both desktop and mobile
    if (ttsContentElementRef.current) {
      ttsContentElementRef.current.textContent = "";
    }
    if (ttsMobileContentElementRef.current) {
      ttsMobileContentElementRef.current.textContent = "";
    }
  }, []);

  // Helper function to cancel current stream
  const cancelCurrentStream = useCallback(() => {
    // Reset texts waiting for audio
    textsWaitingForAudioRef.current = [];
    hasReceivedAudioForCurrentTextRef.current = false;
    // Reset waiting state and clear pending tool events
    isWaitingForSpeakAudioRef.current = false;
    pendingToolEventsRef.current = [];
    // Note: WebSocket connection remains open, we just stop processing the current stream
  }, []);

  // ---------- TTS QUEUE MANAGEMENT ----------
  const ttsQueueManager = useTtsQueueManager({
    onContentReady: (content: string) => {
      resetTtsContentDisplay();
      ttsFullContentRef.current = content;
      ttsCharIndexRef.current = 0;
      setTtsFullContent(content);
    },
    onContentComplete: () => {
      // Content display is complete, ready for next
      timeoutManager.startTimeoutCountdown();
    },
    onClearContent: () => {
      timeoutManager.startTimeoutCountdown();
      // Content display is complete, ready for next
      setTimeout(() => {
        handleClearContent();
      }, 1000);
    },
    onClearCurrentContent: () => {
      resetTtsContentDisplay();
      setTtsFullContent("");
    },
  });

  useEffect(() => {
    markCurrentContentCompleteRef.current =
      ttsQueueManager.markCurrentContentComplete;
  }, [ttsQueueManager.markCurrentContentComplete]);

  const isClearingContentRef = useRef(false);
  const handleClearContent = useCallback(() => {
    // Prevent multiple calls
    if (isClearingContentRef.current) {
      return;
    }

    isClearingContentRef.current = true;
    ttsQueueManager.clearQueue();
    setTtsFullContent("");
    resetTtsContentDisplay();

    // Reset flag after a short delay
    setTimeout(() => {
      isClearingContentRef.current = false;
    }, 300);
  }, [ttsQueueManager, resetTtsContentDisplay]);

  // Use ref to avoid infinite loop in useEffect
  const handleClearContentRef = useRef(handleClearContent);
  useEffect(() => {
    handleClearContentRef.current = handleClearContent;
  }, [handleClearContent]);

  // ---------- MANAGERS ----------
  const audioManager = useAudioManager({
    filterConfig: phoneSimConfig,
    onTTSAudioStart: () => {
      setIsSpeaking(true);
      ttsQueueManager.markTtsAudioReady();

      if (isWaitingForSpeakAudioRef.current) {
        while (pendingToolEventsRef.current.length > 0) {
          const toolEvent = pendingToolEventsRef.current[0];
          if (isMapTool(toolEvent?.tool)) {
            pendingToolEventsRef.current.shift();
            handleFunctionStreamCall(toolEvent);
          } else if (isNewsTool(toolEvent?.tool)) {
            break;
          } else {
            break;
          }
        }
      }
    },
    onTTSAudioComplete: () => {
      ttsQueueManager.removeItemWhenAudioComplete();
      while (pendingToolEventsRef.current.length > 0) {
        const toolEvent = pendingToolEventsRef.current[0];
        if (isNewsTool(toolEvent?.tool)) {
          pendingToolEventsRef.current.shift();
          handleFunctionStreamCall(toolEvent);
        } else if (isMapTool(toolEvent?.tool)) {
          pendingToolEventsRef.current.shift();
          handleFunctionStreamCall(toolEvent);
        } else {
          break;
        }
      }

      if (pendingToolEventsRef.current.length === 0) {
        isWaitingForSpeakAudioRef.current = false;
      }
    },
    onAllAudioComplete: () => {
      setIsSpeaking(false);

      if (!isInSpeakSessionRef.current) {
        setIsThinking(false);
      }

      // Survey finished — audio has played to completion, now cleanup
      if (isSurveyFinishedRef.current) {
        console.log("🏁 [FINISH] Survey audio complete - disconnecting");
        clientLogger.log("survey_complete_disconnect", {});
        isSurveyFinishedRef.current = false;
        setCallPhase('ended');
        setActiveTab('conversation');
        wakewordManager.stopListening();
        timeoutManager.resetTimeoutCountdown();
        webSocketAudioManager.disconnect("survey_complete", "session_close_event");
        void clientLogger.flush(false);
        return;
      }

      // Enable mic streaming after audio finishes (especially greeting)
      // This prevents echo from bot audio being sent to STT
      if (isStartRef.current) {
        console.log("🎤 [MIC] Enabling mic streaming after audio complete");
        webSocketAudioManager.updateListeningState(true);
      }

      // Notify server that all audio has finished playing (with turn_id for validation)
      // Diagnostics let backend prove client actually heard the bot (vs. just
      // received frames over WS). Reset counters after sending so the next
      // bot speech segment reports its own stats.
      const turnId = currentTurnIdRef.current;
      const diag = audioManager.getDiagnostics();
      const firstAt = firstAudioChunkAtRef.current;
      const lastAt = lastAudioChunkAtRef.current;
      const ackPayload = {
        turn_id: turnId,
        last_sequence_played: lastReceivedAudioSeqRef.current,
        chunks_received: audioChunksReceivedRef.current,
        bytes_received: audioBytesReceivedRef.current,
        receive_window_ms:
          firstAt != null && lastAt != null ? lastAt - firstAt : null,
        ...diag,
      };
      clientLogger.log("playback_completed", ackPayload);
      if (turnId != null) {
        console.log(
          `📢 [PLAYBACK] All audio complete - sending playback.completed (turn_id=${turnId}, seq=${lastReceivedAudioSeqRef.current}, ctx=${diag.audio_context_state})`
        );
        webSocketAudioManager.sendEvent("response.playback.completed", ackPayload);
      } else {
        console.log(`📢 [PLAYBACK] All audio complete - skipping playback.completed (no turn_id)`);
      }
      // Reset for next segment
      lastReceivedAudioSeqRef.current = null;
      audioChunksReceivedRef.current = 0;
      audioBytesReceivedRef.current = 0;
      firstAudioChunkAtRef.current = null;
      lastAudioChunkAtRef.current = null;
    },
  });

  // Transport Manager - handles WebSocket communication
  const webSocketAudioManager = useTransportManager({
    filterConfig: phoneSimConfig,
    inputSampleRate: phoneSimInputRate,
    sttModel,
    onConnectionStart: () => {
      setIsConnecting(true);
      setIsConnectionComplete(false);
    },
    onConnectionComplete: () => {
      setIsConnectionComplete(true);
    },
    isSpeaking: isSpeaking, // Pass speaking state to prevent sending audio during playback
    preferredTransport: "websocket",
    language: i18n.language || "vi",
    useCase: selectedScenario,
    mode: callMode,
    onAudioReceived: (audioData: ArrayBuffer) => {
      // Check if we're in streaming audio mode
      if (isStreamingAudioModeRef.current) {
        // Process as streaming audio chunk (raw PCM 16-bit)
        audioManager.processStreamingAudioChunk(audioData);
        return;
      }

      // Standard (non-streaming) audio processing
      if (
        textsWaitingForAudioRef.current.length > 0 &&
        !hasReceivedAudioForCurrentTextRef.current
      ) {
        const textToMark = textsWaitingForAudioRef.current[0];
        if (textToMark) {
          audioManager.setCurrentPlayingText(textToMark);
          textsWaitingForAudioRef.current.shift();
          hasReceivedAudioForCurrentTextRef.current = true;
        }
      }

      audioManager.processAudioFromArrayBuffer(audioData);
    },
    playbackStream: audioManager.getPlaybackStream(),
    isThinking: isThinking,
    onStreamEvent: (eventData: any) => {
      // Handle Voice Live API events (use "type" field)
      if (eventData.type) {
        console.log(`📥 [STREAM_EVENT] type=${eventData.type}`, eventData);
        switch (eventData.type) {
          case "input_audio_buffer.speech_started":
            // User started speaking — do NOT stop audio here.
            // Wait for backend to confirm interrupt via response.cancelled.
            // AgentSession applies min_interruption_duration & min_interruption_words first.
            console.log("🎙️ [SPEECH_STARTED] User started speaking (waiting for backend interrupt)");
            break;

          case "response.audio.clear":
            // Backend wants client to immediately stop playing audio (reduce echo for STT)
            console.log("🔇 [AUDIO_CLEAR] Clearing audio buffer to reduce echo");
            audioManager.stopAudio();
            if (isStreamingAudioModeRef.current) {
              audioManager.stopStreamingAudio();
            }
            ttsQueueManager.clearQueue();
            break;

          case "response.audio.paused":
            // Backend paused audio (possible false interruption — user may just be saying "ừ", "hmm")
            console.log("⏸️ [AUDIO_PAUSED] Audio paused — waiting for backend decision");
            break;

          case "response.audio.resumed":
            // Backend confirmed false interruption — resume playback
            console.log("▶️ [AUDIO_RESUMED] False interruption — resuming audio");
            break;

          case "response.created":
            console.log(`🧠 [RESPONSE.CREATED] Starting to think`);
            setIsThinking(true);
            isInSpeakSessionRef.current = true;
            timeoutManager.startTimeoutCountdown();
            break;

          case "conversation.item.input_audio_transcription.completed":
            // STT transcription completed — show single final message
            if (eventData.transcript) {
              // If there's a pending placeholder, replace it
              if (hasPendingUserMessageRef.current) {
                setConversationHistory(prev => {
                  const updated = [...prev];
                  for (let i = updated.length - 1; i >= 0; i--) {
                    if (updated[i].role === "user" && updated[i].isPending) {
                      updated[i] = { role: "user", content: eventData.transcript, isPending: false };
                      return updated;
                    }
                  }
                  // Pending ref was true but no pending message found — just append
                  return [...prev, { role: "user", content: eventData.transcript }];
                });
                hasPendingUserMessageRef.current = false;
              } else {
                // No pending — append only if last message isn't the same user text
                setConversationHistory(prev => {
                  const last = prev[prev.length - 1];
                  if (last && last.role === "user" && last.content === eventData.transcript) {
                    return prev; // skip duplicate
                  }
                  return [...prev, { role: "user", content: eventData.transcript }];
                });
              }
            }
            break;

          case "response.audio.delta":
            {
              const base64Audio = eventData.delta;
              const sr = eventData.sample_rate || 16000;
              const seq = (eventData as { sequence?: number }).sequence;
              if (base64Audio) {
                // Decode base64 to ArrayBuffer
                const binaryStr = atob(base64Audio);
                const bytes = new Uint8Array(binaryStr.length);
                for (let i = 0; i < binaryStr.length; i++) {
                  bytes[i] = binaryStr.charCodeAt(i);
                }
                // Diagnostics: track delivery so we can prove the client
                // actually received the audio frames the server sent.
                const nowMs = Date.now();
                if (audioChunksReceivedRef.current === 0) {
                  firstAudioChunkAtRef.current = nowMs;
                  clientLogger.log("audio_first_chunk_received", {
                    sequence: seq ?? null,
                    sample_rate: sr,
                    bytes: bytes.length,
                  });
                }
                audioChunksReceivedRef.current += 1;
                audioBytesReceivedRef.current += bytes.length;
                if (typeof seq === "number") {
                  lastReceivedAudioSeqRef.current = seq;
                }
                lastAudioChunkAtRef.current = nowMs;
                audioManager.processAudioFromArrayBuffer(bytes.buffer, sr);
              }
            }
            break;

          case "response.audio.done":
            console.log(`🔇 [AUDIO.DONE] All audio chunks received`);
            break;

          case "response.text.delta":
            {
              const delta = eventData.delta;
              console.log(`📝 [TEXT.DELTA] Received text chunk: "${delta}"`);
              timeoutManager.startTimeoutCountdown();
              setIsThinking(false);
              setIsSpeaking(true);
              // Streaming: update single assistant message in-place
              if (delta) {
                setConversationHistory(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last && last.role === "assistant" && !last.isComplete) {
                    // Append to existing streaming message
                    updated[updated.length - 1] = { ...last, content: (last.content || "") + delta };
                  } else {
                    // Start new streaming message
                    updated.push({ role: "assistant", content: delta, isComplete: false });
                  }
                  return updated;
                });
              }
            }
            break;

          case "response.text.done":
            {
              const fullText = eventData.text;
              const source = eventData.source;
              console.log(`📝 [TEXT.DONE] source=${source} | lang=${eventData.language} | text="${fullText}"`);

              // start:welcome — greeting, first message
              if (source === "start") {
                console.log(`👋 [START] Welcome message (${eventData.language})`);
              }

              // next:ask — regular question
              if (source === "next") {
                console.log(`➡️ [NEXT] Ask question`);
              }

              // interrupted:ask — barge-in response
              if (source === "interrupted") {
                console.log(`🔄 [INTERRUPTED] Barge-in response`);
              }

              // finished:goodbye — stop listening immediately, disconnect after audio plays
              if (source === "finished") {
                console.log("🏁 [FINISHED] Goodbye — will disconnect after audio completes");
                isSurveyFinishedRef.current = true;
                webSocketAudioManager.updateListeningState(false);
              }

              // Finalize: replace streaming message with complete text
              if (fullText) {
                setConversationHistory(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last && last.role === "assistant" && !last.isComplete) {
                    updated[updated.length - 1] = { ...last, content: fullText, isComplete: true };
                  } else {
                    updated.push({ role: "assistant", content: fullText, isComplete: true });
                  }
                  return updated;
                });
              }
            }
            break;

          case "response.done":
            {
              const turnId = eventData?.turn_id ?? eventData?.data?.turn_id ?? null;
              console.log(`📦 [RESPONSE.DONE] Server finished sending all audio (turn_id=${turnId})`);
              currentTurnIdRef.current = turnId;
              setIsSpeaking(false);
              audioManager.markResponseDone();
            }
            break;

          case "conversation.item.completed":
            console.log("=== CONVERSATION COMPLETED ===", eventData);
            if (eventData.report) {
              setSurveyReport(eventData.report);
            }

            // Clear the timeout fallback since we received the report
            if (surveyReportTimeoutRef.current) {
              clearTimeout(surveyReportTimeoutRef.current);
              surveyReportTimeoutRef.current = null;
            }

            // If user initiated hang up, clear waiting state
            if (isWaitingForSurveyReportRef.current) {
              setIsWaitingForSurveyReport(false);
              isWaitingForSurveyReportRef.current = false;
            }
            break;

          case "response.cancelled":
            {
              console.log(`🚫 [RESPONSE.CANCELLED] reason=${eventData.reason}`);
              // Response was cancelled (e.g. user interrupt) - stop audio and reset states
              audioManager.stopAudio();
              if (isStreamingAudioModeRef.current) {
                audioManager.stopStreamingAudio();
                isStreamingAudioModeRef.current = false;
              }
              setIsThinking(false);
              setIsSpeaking(false);
              isWaitingForSpeakAudioRef.current = false;
              ttsQueueManager.clearQueue();
            }
            break;

          case "error":
            {
              const errorDetail = eventData.error || {};
              console.error(`❌ [ERROR] code=${errorDetail.code} message=${errorDetail.message}`);
              setIsThinking(false);
              setIsSpeaking(false);
              isInSpeakSessionRef.current = false;
              isWaitingForSpeakAudioRef.current = false;
              pendingToolEventsRef.current = [];
            }
            break;

          case "input_audio_buffer.speech_stopped":
            console.log("🎙️ [SPEECH_STOPPED] User stopped speaking");
            break;

          case "input_audio_buffer.committed":
            console.log("📦 [COMMITTED] Audio buffer committed");
            break;

          case "session.created":
            console.log(`✅ [SESSION.CREATED] session_id=${eventData.session_id}`);
            backendSessionIdRef.current = eventData.session_id;
            setDisplayedCallId(eventData.session_id);
            break;

          case "session.closed":
            console.log("=== SESSION CLOSED ===", eventData);
            // Mark survey as finished — disconnect after audio completes
            isSurveyFinishedRef.current = true;
            // Belt-and-suspenders: if neither onAllAudioComplete nor the
            // ws-close fallback transitions us to 'ended' within 8s (audio
            // playback stuck, tab throttled, streaming chunks not tracked
            // in pendingSourcesRef, etc.), force the transition so the
            // report popup still opens for the user — especially for
            // shared-link visitors who can't manually hang up.
            setTimeout(() => {
              if (callPhaseRef.current === 'active' && isSurveyFinishedRef.current) {
                console.log("🏁 [FINISH] session.closed timeout — forcing callPhase=ended");
                clientLogger.log("survey_complete_forced_end", {
                  reason: "session_closed_timeout",
                });
                isSurveyFinishedRef.current = false;
                setCallPhase('ended');
                setActiveTab('conversation');
                void clientLogger.flush(false);
              }
            }, 8000);
            break;
        }
        return;
      }

      // Handle legacy events (use "event_type" field)
      if (eventData.event_type) {
        console.log(`📥 [LEGACY_EVENT] type=${eventData.event_type}`);
      }
      switch (eventData.event_type) {
        case "resume_audio":
          // Backend decided to ignore the interrupt - resume audio playback
          console.log("▶️ [RESUME_AUDIO] Interrupt ignored - resuming audio");
          setIsSpeaking(true);
          // Remove pending user message if any (interrupt was ignored)
          if (hasPendingUserMessageRef.current) {
            setConversationHistory(prev => {
              // Remove the last pending user message
              const updated = [...prev];
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].role === "user" && updated[i].isPending) {
                  updated.splice(i, 1);
                  break;
                }
              }
              return updated;
            });
            hasPendingUserMessageRef.current = false;
          }
          break;

        case "user_message_pending":
          // Backend detected interrupt - add empty user message placeholder
          // Only add if there's no pending message already (avoid duplicate)
          if (!hasPendingUserMessageRef.current) {
            console.log("📝 [UI] user_message_pending - adding empty user message box");
            hasPendingUserMessageRef.current = true;
            setConversationHistory(prev => [...prev, { role: "user", content: "...", isPending: true }]);
          }
          break;

        case "tool":
          if (isWaitingForSpeakAudioRef.current) {
            pendingToolEventsRef.current.push({
              ...eventData.data,
            });
          } else {
            handleFunctionStreamCall({
              ...eventData.data,
            });
          }
          break;

        case "clear_audio":
        case "stop_audio":
          // Backend requests to clear/stop audio buffer immediately (e.g. on interrupt)
          console.log("🔇 [CLEAR_AUDIO] Backend requested audio clear");
          audioManager.stopAudio();
          if (isStreamingAudioModeRef.current) {
            audioManager.stopStreamingAudio();
            isStreamingAudioModeRef.current = false;
          }
          setIsSpeaking(false);
          isWaitingForSpeakAudioRef.current = false;
          ttsQueueManager.clearQueue();
          break;

        case "state_message":
          // No action needed
          break;

        case "error":
          setIsThinking(false);
          setIsSpeaking(false);
          isInSpeakSessionRef.current = false;
          isWaitingForSpeakAudioRef.current = false;
          pendingToolEventsRef.current = [];
          break;

        case "ignore":
          setIsThinking(false);
          setIsSpeaking(false);
          isInSpeakSessionRef.current = false;
          isWaitingForSpeakAudioRef.current = false;
          pendingToolEventsRef.current = [];
          break;

        case "done":
          textsWaitingForAudioRef.current = [];
          isWaitingForSpeakAudioRef.current = false;
          pendingToolEventsRef.current = [];

          timeoutManager.handleApiComplete();
          break;

        default:
          console.warn(`Unknown event type: ${eventData.event_type}`);
          break;
      }
    },
    onConnectError: () => {
      // Play disconnect sound on connection error
      const disconnectSound = new Audio("/call disconnect.wav");
      disconnectSound.play().catch((err) => console.warn("Could not play disconnect sound:", err));

      isStartRef.current = false;
      setIsStart(false);
      wakewordManager.stopListening();
      timeoutManager.resetTimeoutCountdown();
      setIsConnecting(false);
      setIsConnectionComplete(false);
      // Reset callPhase to initial so user can try again (fixes stuck ringing/active screen)
      setCallPhase('initial');
      // Stop ringtone if playing
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
        ringtoneRef.current = null;
      }
      setNotificationState({
        title: "Đã xảy ra lỗi kết nối!",
        message: "Vui lòng thử lại.",
      });
    },
  });

  // ---------- HELPER FUNCTION FOR SENDING EVENTS ----------
  const updateToolState = useCallback((eventType: string, data?: any) => {
    webSocketAudioManager.sendEvent(eventType, data);
  }, []);

  const wakewordManager = useWakewordManager({
    onWakewordDetected: () => {
      newsManager.handlePauseNews();

      if (!isStartRef.current) {
        start();
      } else {
        wakewordManager.startListening();
        timeoutManager.startTimeoutCountdown();
      }
    },
    onSpeechStart: () => {
      timeoutManager.startTimeoutCountdown();
    },
  });

  const timeoutManager = useTimeoutManager();

  // Fallback finish: server may close the WebSocket before client's audio
  // playback finishes (network buffering, AudioContext scheduling slack, etc).
  // When WS goes connected→disconnected while we're still in 'active' and a
  // goodbye/session.closed has already arrived, force the transition so the
  // report popup opens — otherwise the UI can hang in 'active' with no path
  // to 'ended' (and onAllAudioComplete may never fire).
  useEffect(() => {
    if (
      !webSocketAudioManager.isConnected &&
      callPhase === 'active' &&
      isSurveyFinishedRef.current
    ) {
      console.log("🏁 [FINISH] WS closed before audio complete — forcing callPhase=ended");
      clientLogger.log("survey_complete_forced_end", {
        reason: "ws_closed_before_audio_complete",
      });
      isSurveyFinishedRef.current = false;
      setCallPhase('ended');
      setActiveTab('conversation');
      wakewordManager.stopListening();
      timeoutManager.resetTimeoutCountdown();
      void clientLogger.flush(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketAudioManager.isConnected, callPhase]);

  const newsManager = useNewsManager({
    onNewsPlayback: () => {
      setIsNewsPlayingOnMap(false);
      setCurrentNewsTitle("");
    },
  });

  useEffect(() => {
    if (newsManager.summaryContent && newsManager.isPlayingSummary) {
      setFullContent(newsManager.summaryContent);
      resetNewsContentDisplay();
      setCurrentNewsTitle(newsManager.summaryTitle || "Tóm tắt tin tức");
      return;
    }

    if (
      newsManager.currentPageAudio &&
      newsManager.currentPageAudio.length > 0
    ) {
      const currentNews =
        newsManager.currentPageAudio[newsManager.currentPageIndex - 1];
      if (currentNews) {
        const content = currentNews.content
          ? `${currentNews.source} - ${currentNews.published_date} - ${currentNews.title} - ${currentNews.content}`
          : "";
        setFullContent(content);
        resetNewsContentDisplay();
        setCurrentNewsTitle(currentNews.title || "");
      }
    }
  }, [
    newsManager.currentPageAudio,
    newsManager.currentPageIndex,
    newsManager.isPlayingSummary,
    newsManager.isPlayingNews,
    newsManager.summaryContent,
    newsManager.summaryTitle,
    resetNewsContentDisplay,
  ]);

  useEffect(() => {
    if (newsManager.audioDuration > 0) {
      setNewsDuration(newsManager.audioDuration);
    }
  }, [newsManager.audioDuration]);

  useEffect(() => {
    if (
      newsManager.isPlayingNews &&
      !newsManager.isNewsPaused &&
      newsManager.audioDuration === 0
    ) {
      setNewsDuration(245);
    }
  }, [
    newsManager.isPlayingNews,
    newsManager.isNewsPaused,
    newsManager.audioDuration,
  ]);

  // Calculate dynamic timing based on content length and audio duration
  const calculateCharacterTiming = useCallback(() => {
    if (
      !fullContent ||
      !newsManager.audioDuration ||
      newsManager.audioDuration === 0
    ) {
      return 120;
    }

    const totalCharacters = fullContent.length;
    const audioDurationMs = newsManager.audioDuration * 1000;
    const timingPerCharacter = audioDurationMs / totalCharacters;

    const words = fullContent.split(/\s+/).length;
    const wordsPerMinute = (words / newsManager.audioDuration) * 60;

    return Math.max(30, Math.min(300, timingPerCharacter));
  }, [fullContent, newsManager.audioDuration]);

  useEffect(() => {
    const isPlaying = newsManager.isPlayingNews || newsManager.isPlayingSummary;
    const contentElement = newsContentElementRef.current;

    // Check if fullContent changed (new news item)
    const isNewContent = lastFullContentRef.current !== fullContent;
    if (isNewContent) {
      lastFullContentRef.current = fullContent;
    }

    if (
      fullContent &&
      isPlaying &&
      !newsManager.isNewsPaused &&
      !isCharacterDisplayingRef.current &&
      contentElement
    ) {
      // Clear any existing interval
      if (newsIntervalRef.current) {
        clearInterval(newsIntervalRef.current);
        newsIntervalRef.current = null;
      }

      // Calculate start index based on audio time (for resume) or start from beginning
      let startIndex = 0;
      // If content changed, always start from beginning
      if (isNewContent) {
        newsCharIndexRef.current = 0;
        contentElement.textContent = "";
      } else if (newsManager.audioDuration > 0) {
        // Calculate char index based on current audio time to sync with audio (resume case)
        const currentAudioTime = newsManager.getCurrentAudioTime();
        const progress = Math.min(
          currentAudioTime / newsManager.audioDuration,
          1
        );
        startIndex = Math.floor(fullContent.length * progress);
        // Ensure we don't exceed content length
        startIndex = Math.min(startIndex, fullContent.length);
        // Restore the displayed content up to current position
        contentElement.textContent = fullContent.substring(0, startIndex);
      } else {
        // No audio duration yet - start from beginning
        newsCharIndexRef.current = 0;
        contentElement.textContent = "";
      }

      isCharacterDisplayingRef.current = true;
      newsCharIndexRef.current = startIndex;

      const characterTiming = calculateCharacterTiming();
      const content = fullContent; // Capture content in closure

      const interval = setInterval(() => {
        const newIndex = newsCharIndexRef.current + 1;
        newsCharIndexRef.current = newIndex;

        const displayedText = content.substring(0, newIndex);
        if (contentElement) {
          contentElement.textContent = displayedText;
          // Auto-scroll to bottom
          contentElement.scrollTop = contentElement.scrollHeight;
        }

        if (newIndex >= content.length) {
          isCharacterDisplayingRef.current = false;
          if (newsIntervalRef.current) {
            clearInterval(newsIntervalRef.current);
            newsIntervalRef.current = null;
          }
        }
      }, characterTiming);

      newsIntervalRef.current = interval;

      return () => {
        if (newsIntervalRef.current) {
          clearInterval(newsIntervalRef.current);
          newsIntervalRef.current = null;
        }
        isCharacterDisplayingRef.current = false;
      };
    }

    if (newsManager.isNewsPaused && isCharacterDisplayingRef.current) {
      // Pause: stop interval but keep current content and position
      isCharacterDisplayingRef.current = false;
      if (newsIntervalRef.current) {
        clearInterval(newsIntervalRef.current);
        newsIntervalRef.current = null;
      }
      // Don't reset newsCharIndexRef or content - keep them for resume
    }
  }, [
    newsManager.isPlayingNews,
    newsManager.isPlayingSummary,
    newsManager.isNewsPaused,
    fullContent,
    calculateCharacterTiming,
    newsManager.audioDuration,
    newsManager.getCurrentAudioTime,
  ]);

  // Dynamic timing adjustment based on audio progress
  useEffect(() => {
    const isPlaying = newsManager.isPlayingNews || newsManager.isPlayingSummary;
    const contentElement = newsContentElementRef.current;

    if (
      fullContent &&
      isPlaying &&
      !newsManager.isNewsPaused &&
      isCharacterDisplayingRef.current &&
      newsManager.audioDuration > 0 &&
      contentElement
    ) {
      const currentTiming = calculateCharacterTiming();
      const remainingCharacters = fullContent.length - newsCharIndexRef.current;
      const currentAudioTime = newsManager.getCurrentAudioTime();
      const remainingTime = newsManager.audioDuration - currentAudioTime;

      if (remainingTime > 0 && remainingCharacters > 0) {
        const newTiming = (remainingTime * 1000) / remainingCharacters;
        const timingDifference =
          Math.abs(newTiming - currentTiming) / currentTiming;

        if (timingDifference > 0.2 && newsIntervalRef.current) {
          // Restart with new timing
          isCharacterDisplayingRef.current = false;
          clearInterval(newsIntervalRef.current);
          newsIntervalRef.current = null;

          setTimeout(() => {
            if (contentElement && isPlaying && !newsManager.isNewsPaused) {
              isCharacterDisplayingRef.current = true;
              const content = fullContent;

              newsIntervalRef.current = setInterval(() => {
                const newIndex = newsCharIndexRef.current + 1;
                newsCharIndexRef.current = newIndex;

                const displayedText = content.substring(0, newIndex);
                if (contentElement) {
                  contentElement.textContent = displayedText;
                  contentElement.scrollTop = contentElement.scrollHeight;
                }

                if (newIndex >= content.length) {
                  isCharacterDisplayingRef.current = false;
                  if (newsIntervalRef.current) {
                    clearInterval(newsIntervalRef.current);
                    newsIntervalRef.current = null;
                  }
                }
              }, newTiming);
            }
          }, 10);
        }
      }
    }
  }, [
    newsManager.audioDuration,
    newsManager.isPlayingNews,
    newsManager.isPlayingSummary,
    newsManager.isNewsPaused,
    fullContent,
    calculateCharacterTiming,
    newsManager.getCurrentAudioTime,
  ]);

  useEffect(() => {
    if (fullContent) {
      resetNewsContentDisplay();
    }
  }, [fullContent, resetNewsContentDisplay]);

  useEffect(() => {
    const content = ttsFullContent;
    const hasElement =
      ttsContentElementRef.current || ttsMobileContentElementRef.current;

    if (
      content &&
      !isTtsCharacterDisplayingRef.current &&
      hasElement &&
      displayedContentIdRef.current !== content
    ) {
      if (ttsIntervalRef.current) {
        clearInterval(ttsIntervalRef.current);
        ttsIntervalRef.current = null;
      }

      ttsFullContentRef.current = content;
      displayedContentIdRef.current = content;
      isTtsCharacterDisplayingRef.current = true;
      setIsTtsCharacterDisplaying(true);
      hasMarkedCompleteRef.current = false;
      ttsCharIndexRef.current = 0;

      const ttsCharacterTiming = 60;
      const desktopElement = ttsContentElementRef.current;
      const mobileElement = ttsMobileContentElementRef.current;

      if (desktopElement) {
        desktopElement.textContent = "";
      }
      if (mobileElement) {
        mobileElement.textContent = "";
      }

      const interval = setInterval(() => {
        const newIndex = ttsCharIndexRef.current + 1;
        ttsCharIndexRef.current = newIndex;

        const displayedText = content.substring(0, newIndex);

        if (desktopElement) {
          desktopElement.textContent = displayedText;
        }
        if (mobileElement) {
          if (isMobile && displayedText.length > 35) {
            const lastPortion = displayedText.slice(-35);
            const spaceIndex = lastPortion.indexOf(" ");
            const finalText =
              spaceIndex > 0 && spaceIndex < 10
                ? lastPortion.slice(spaceIndex + 1)
                : lastPortion;
            mobileElement.textContent = finalText;
          } else {
            mobileElement.textContent = displayedText;
          }
        }

        if (newIndex >= content.length) {
          isTtsCharacterDisplayingRef.current = false;
          setIsTtsCharacterDisplaying(false);

          if (!hasMarkedCompleteRef.current) {
            hasMarkedCompleteRef.current = true;
            markCurrentContentCompleteRef.current();
          }

          if (ttsIntervalRef.current) {
            clearInterval(ttsIntervalRef.current);
            ttsIntervalRef.current = null;
          }
        }
      }, ttsCharacterTiming);

      ttsIntervalRef.current = interval;

      return () => {
        if (ttsIntervalRef.current) {
          clearInterval(ttsIntervalRef.current);
          ttsIntervalRef.current = null;
        }
        isTtsCharacterDisplayingRef.current = false;
        setIsTtsCharacterDisplaying(false);
      };
    }
  }, [ttsFullContent, isMobile]);

  useEffect(() => {
    if (isTtsCharacterDisplaying) {
      setIsTtsDisplaying(true);
    } else {
      const timeout = setTimeout(() => {
        setIsTtsDisplaying(false);
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isTtsCharacterDisplaying]);

  const mapManager = useMapManager({
    onApiStart: timeoutManager.handleApiStart,
    onApiComplete: timeoutManager.handleApiComplete,
  });

  useEffect(() => {
    mapManager.getCurrentLocation();
  }, []);

  // ---------- DERIVED STATE ----------
  const isListening =
    webSocketAudioManager.isConnected && wakewordManager.isListening;
  const isMapVisible = mapManager.isMapVisible;
  const isNewsVisible = newsManager.isNewsVisible;

  useEffect(() => {
    if (mapManager.currentLocation) {
      const locationMatch = mapManager.currentLocation.match(
        /^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/
      );
      if (locationMatch && locationMatch.length === 3) {
        const lat = parseFloat(locationMatch[1]);
        const lng = parseFloat(locationMatch[2]);

        if (!isNaN(lat) && !isNaN(lng) && webSocketAudioManager.isConnected) {
          webSocketAudioManager.sendEvent("location_update", {
            latitude: lat,
            longitude: lng,
          });
        }
      }
    }
  }, [mapManager.currentLocation, webSocketAudioManager.isConnected]);

  useEffect(() => {
    if (webSocketAudioManager.isConnected && selectedTool) {
      webSocketAudioManager.sendEvent("update_state", {
        device_state: selectedTool,
      });
    }
  }, [selectedTool, webSocketAudioManager.isConnected]);

  // conversation.start is sent explicitly in start(), not via useEffect

  useEffect(() => {
    if (!isListening) {
      webSocketAudioManager.updateListeningState(false);
      handleClearContentRef.current();
      setIsThinking(false); // Reset thinking state when not listening
      setIsSpeaking(false); // Reset speaking state when not listening
      // Clear TTS content from DOM - both desktop and mobile
      if (ttsContentElementRef.current) {
        ttsContentElementRef.current.textContent = "";
      }
      if (ttsMobileContentElementRef.current) {
        ttsMobileContentElementRef.current.textContent = "";
      }
    }
  }, [isListening]);

  useEffect(() => {
    if (isListening && !isThinking) {
      updateToolState(WS_EVENTS.LISTENING);
    } else {
      updateToolState(WS_EVENTS.LISTENING_STOP);
    }
  }, [isListening, isThinking]);

  useEffect(() => {
    const playbackStream = audioManager.getPlaybackStream();
    if (playbackStream) {
      webSocketAudioManager.updatePlaybackStream(playbackStream);
    }
  }, [audioManager, webSocketAudioManager]);

  // Connection is now triggered by handleBegin button click, not on mount

  // Track initialization state - only wait for models to be ready
  useEffect(() => {
    if (!wakewordManager.isLoadingModels) {
      setIsInitializing(false);
    }
  }, [wakewordManager.isLoadingModels]);

  // Install browser lifecycle hooks (pagehide / beforeunload / visibility)
  // once on mount so the client logger can flush via Beacon during teardown.
  useEffect(() => {
    installLifecycleHooks();
    clientLogger.log("page_mount", { url: window.location.href });
    return () => {
      clientLogger.log("page_unmount", {});
      void clientLogger.flush(true);
    };
  }, []);

  // ---------- START/STOP FUNCTIONS ----------
  const start = useCallback(async () => {
    // Reset per-session refs/queues so state from a prior conversation
    // (e.g. lingering isSurveyFinishedRef=true after an interrupted finish)
    // cannot leak into this one and trigger an immediate disconnect.
    isSurveyFinishedRef.current = false;
    isWaitingForSpeakAudioRef.current = false;
    isInSpeakSessionRef.current = false;
    isStreamingAudioModeRef.current = false;
    pendingToolEventsRef.current = [];
    textsWaitingForAudioRef.current = [];
    hasReceivedAudioForCurrentTextRef.current = false;
    currentTurnIdRef.current = null;
    ttsQueueManager.clearQueue();

    clientLogger.log("user_start", { selected_tool: selectedTool });
    webSocketAudioManager.updateListeningState(true);

    isStartRef.current = true;
    setIsStart(true);
    audioManager.connectTTS();

    // Signal backend to start conversation — triggers greeting
    webSocketAudioManager.sendEvent("conversation.start", {});

    await wakewordManager.startListening();
    if (selectedTool !== "news") {
      timeoutManager.startTimeoutCountdown();
    }
  }, [
    audioManager,
    webSocketAudioManager,
    wakewordManager,
    selectedTool,
    timeoutManager,
    ttsQueueManager,
  ]);

  const stop = useCallback(() => {
    // Stop wakeword listening
    wakewordManager.stopListening();

    // Reset timeout
    timeoutManager.resetTimeoutCountdown();

    // Update listening state
    webSocketAudioManager.updateListeningState(false);

    // Reset start state
    isStartRef.current = false;
    setIsStart(false);

    // Reset thinking and speaking state
    setIsThinking(false);
    setIsSpeaking(false);

    // Cancel current stream
    cancelCurrentStream();

    // Clear content
    handleClearContentRef.current();

    // Reset audio
    audioManager.resetAudioCompletely();

    isInSpeakSessionRef.current = false;
  }, [
    wakewordManager,
    timeoutManager,
    webSocketAudioManager,
    cancelCurrentStream,
    audioManager,
  ]);

  // VAD-based sendToSTT removed - AudioWorkletNode sends audio chunks directly via WebSocket
  const handleFunctionStreamCall = (functionCall: any) => {
    const { tool, category, locations, destination, name, content_type } =
      functionCall;

    switch (tool) {
      case MAP_TOOLS.NAVIGATE_TO_ADDRESS:
        if (destination) {
          // Set map tool and show map
          setSelectedTool("map");
          mapManager.setIsMapVisible(true);
          mapManager.setCurrentNavigationDestination(destination);
          mapManager.setAutocompleteSearch(name);
          setLocationChoices([]);
          timeoutManager.startTimeoutCountdown();

          // Clear news state when switching to map
          newsManager.setIsNewsVisible(false);
          newsManager.stopAllAudio();

          // Send navigation_set event
          updateToolState(WS_EVENTS.NAVIGATION_SET);
        }
        break;

      case MAP_TOOLS.OPEN_MAP:
        setSelectedTool("map");
        mapManager.setIsMapVisible(true);
        timeoutManager.startTimeoutCountdown();

        // Clear news state when switching to map
        newsManager.setIsNewsVisible(false);
        newsManager.stopAllAudio();

        // Send map_enabled event
        updateToolState(WS_EVENTS.MAP_ENABLED);
        break;

      case MAP_TOOLS.CLOSE_MAP:
        mapManager.setIsMapVisible(false);
        setSelectedTool("home");
        mapManager.setCurrentNavigationDestination(null);
        mapManager.setPendingNavigationAddress(null);
        setLocationChoices([]); // Clear location choices when closing map

        // Send map_disabled event
        updateToolState(WS_EVENTS.MAP_DISABLED);
        break;

      case MAP_TOOLS.SHOW_ADDRESS_LIST:
        setLocationChoices([]);

        if (locations?.length) {
          setLocationChoices(locations);
        }

        // Show map if not already visible
        setSelectedTool("map");
        mapManager.setIsMapVisible(true);

        // Clear news state when switching to map
        newsManager.setIsNewsVisible(false);
        newsManager.stopAllAudio();

        // Send address_list_shown event
        updateToolState(WS_EVENTS.ADDRESS_LIST_SHOWN);
        break;

      case NEWS_TOOLS.OPEN_NEWS:
        const isSummary = content_type === "tom-tat";

        if (selectedToolRef.current === "map") {
          stop();
          resetNewsContentDisplay();
          newsManager.handleNewsIntent({ category, is_summary: isSummary });
          newsManager.stopAllAudio();
          setIsNewsPlayingOnMap(true);

          // Send news_enabled event (news playing on map)
          updateToolState(WS_EVENTS.NEWS_ENABLED);
          break;
        }

        // Otherwise, show news UI and switch to news tool
        setSelectedTool("news");
        newsManager.setIsNewsVisible(true);
        stop();
        resetNewsContentDisplay();
        newsManager.handleNewsIntent({ category, is_summary: isSummary });
        newsManager.stopAllAudio();
        setIsNewsPlayingOnMap(false);

        // Clear map state when switching to news
        mapManager.setIsMapVisible(false);
        mapManager.setCurrentNavigationDestination(null);
        mapManager.setPendingNavigationAddress(null);

        // Send news_enabled event
        updateToolState(WS_EVENTS.NEWS_ENABLED);
        break;

      case NEWS_TOOLS.CLOSE_NEWS:
        setIsNewsPlayingOnMap(false);

        if (selectedToolRef.current === "map") {
          newsManager.stopAllAudio();

          // Send news_disabled event (news was playing on map)
          updateToolState(WS_EVENTS.NEWS_DISABLED);
          break;
        }

        newsManager.stopAllAudio();
        newsManager.setIsNewsVisible(false);
        setSelectedTool("home");

        // Send news_disabled event
        updateToolState(WS_EVENTS.NEWS_DISABLED);
        break;

      case NEWS_TOOLS.PAUSE_NEWS:
        if (newsManager.isPlayingNews) {
          newsManager.handlePauseNews();

          // Send news_paused event
          updateToolState(WS_EVENTS.NEWS_PAUSED);
        }
        break;

      case NEWS_TOOLS.RESUME_NEWS:
        stop();
        newsManager.handleResumeNews();
        if (selectedToolRef.current !== "map") {
          newsManager.setIsNewsVisible(true);
        }

        // Send news_resumed event
        updateToolState(WS_EVENTS.NEWS_RESUMED);
        break;

      default:
        console.log(`❓ Unknown function: ${tool}`);
        break;
    }
  };

  // ---------- TOOL SWITCHING LOGIC ----------
  const handleToolSwitch = useCallback(
    (newTool: string) => {
      if (
        !(
          selectedTool !== "home" &&
          newTool !== "home" &&
          selectedTool !== newTool
        )
      ) {
        if (newTool === "map") {
          setSelectedTool(newTool);
          mapManager.setIsMapVisible(true);

          // Clear news state when switching to map
          newsManager.stopAllAudio();
          newsManager.setIsNewsVisible(false);
          setIsNewsPlayingOnMap(false);
          // Start timeout for map tool
          timeoutManager.startTimeoutCountdown();
        } else if (newTool === "news") {
          setSelectedTool(newTool);
          newsManager.stopAllAudio();
          newsManager.setIsNewsVisible(true);
          newsManager.handleNewsIntent({});

          // Reset news content display when switching to news
          resetNewsContentDisplay();

          // Clear map state when switching to news
          mapManager.setIsMapVisible(false);
          mapManager.setCurrentNavigationDestination(null);
          mapManager.setPendingNavigationAddress(null);
          setIsNewsPlayingOnMap(false);
          // Stop timeout for news tool
          timeoutManager.resetTimeoutCountdown();
          // Stop listening when switching to news
          wakewordManager.stopListening();
          // Stop any current TTS before starting news
          audioManager.resetAudioCompletely();
        }
      }
    },
    [
      selectedTool,
      audioManager,
      newsManager,
      mapManager,
      timeoutManager,
      resetNewsContentDisplay,
    ]
  );

  // ---------- CLEANUP ----------
  useEffect(() => {
    return () => {
      timeoutManager.cleanup();
    };
  }, [timeoutManager]);

  useEffect(() => {
    return () => {
      // Cleanup TTS interval
      if (ttsIntervalRef.current) {
        clearInterval(ttsIntervalRef.current);
        ttsIntervalRef.current = null;
      }
      // Cleanup news interval
      if (newsIntervalRef.current) {
        clearInterval(newsIntervalRef.current);
        newsIntervalRef.current = null;
      }
      // Cleanup survey report timeout
      if (surveyReportTimeoutRef.current) {
        clearTimeout(surveyReportTimeoutRef.current);
        surveyReportTimeoutRef.current = null;
      }
      cancelCurrentStream();
      webSocketAudioManager.stopAudioProcessing();
    };
  }, []);

  const handleOrbClick = useCallback(() => {
    start();
    newsManager.handlePauseNews();
  }, [start, newsManager]);

  const handleBegin = useCallback(async () => {
    // Show connecting state
    setIsConnecting(true);

    try {
      // Connect WebSocket
      await webSocketAudioManager.connect();

      // Khmer-only deployment: always sync the backend session to `km` on
      // connect, regardless of the UI i18n language. Syncing the UI language
      // (e.g. "vi") here would flip the session language before the greeting
      // fires, making the cached greeting/q0 clip lookup miss (they are Khmer)
      // and the bot would open silently.
      const currentLanguage = "km";
      console.log(`[LANGUAGE_SYNC] Syncing backend session to Khmer on connect: ${currentLanguage}`);
      webSocketAudioManager.sendEvent("language_change", { language: currentLanguage });

      // Connection successful - start ringing phase
      setIsConnecting(false);
      setCallPhase('ringing');

      // Play ringtone in loop
      ringtoneRef.current = new Audio("/ringtone.wav");
      ringtoneRef.current.loop = true;
      ringtoneRef.current.play().catch((err) => console.warn("Could not play ringtone:", err));
    } catch (error) {
      console.error("❌ Failed to connect:", error);
      setIsConnecting(false);
      // Optionally show error notification
      setNotificationState({
        title: "Lỗi kết nối",
        message: "Không thể kết nối đến máy chủ. Vui lòng thử lại.",
      });
    }
  }, [webSocketAudioManager, i18n.language]);

  const handleAnswer = useCallback(() => {
    // Stop ringtone
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current = null;
    }
    // Play call connect sound effect
    const connectSound = new Audio("/call connect.wav");
    connectSound.play().catch((err) => console.warn("Could not play connect sound:", err));

    setCallPhase('active');
    start();
    newsManager.handlePauseNews();
    setIsMicMuted(false);
  }, [start, newsManager]);

  // Shared mode (first call only): skip the initial selector screen entirely
  // so the visitor lands directly on the ringing screen and only has to tap
  // the answer button. Guarded by a ref so a failed connect (which keeps
  // callPhase at 'initial') doesn't loop. After the first call ends,
  // sharedAutoFlow becomes false and we fall back to the manual flow with
  // a use-case picker.
  useEffect(() => {
    if (
      sharedAutoFlow &&
      callPhase === 'initial' &&
      !isConnecting &&
      !hasAutoBegunRef.current
    ) {
      hasAutoBegunRef.current = true;
      void handleBegin();
    }
  }, [sharedAutoFlow, callPhase, isConnecting, handleBegin]);

  const handleCallStart = useCallback(() => {
    // This is now used when already in active phase
    if (callPhase !== 'active') {
      handleBegin();
      return;
    }
    // Play call connect sound effect
    const connectSound = new Audio("/call connect.wav");
    connectSound.play().catch((err) => console.warn("Could not play connect sound:", err));

    start();
    newsManager.handlePauseNews();
    setIsMicMuted(false);
  }, [start, newsManager, callPhase, handleBegin]);

  const playDisconnectSound = useCallback(() => {
    const disconnectSound = new Audio("/call disconnect.wav");
    disconnectSound.play().catch((err) => console.warn("Could not play disconnect sound:", err));
  }, []);

  const handleHangUp = useCallback(async () => {
    // The canonical `session_disconnect_reason` event is emitted from
    // `webSocketAudioManager.disconnect()` below — passing
    // `subcause="hangup_button"` distinguishes the click from any future
    // user-initiated path (e.g. keyboard shortcut). No need for a
    // separate `user_hangup` log line.
    playDisconnectSound();
    // Stop ringtone if still playing
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current = null;
    }

    // Stop audio/wakeword/listening
    stop();
    setIsMicMuted(false);
    setCallPhase('ended');
    setActiveTab('conversation');

    // Send disconnect event then close WebSocket
    webSocketAudioManager.sendEvent('disconnect');
    webSocketAudioManager.disconnect("user_hangup", "hangup_button");
    // Final flush so the user_hangup entry reaches the server before
    // we navigate away from the report screen.
    void clientLogger.flush(false);

    // Fetch report via REST API
    const sessionId = backendSessionIdRef.current;
    if (sessionId) {
      try {
        const backendUrl = resolveBackendUrl();
        const res = await fetch(`${backendUrl}/v1/sessions/${sessionId}/report`, { method: 'POST' });
        if (res.ok) {
          const report = await res.json();
          setSurveyReport(report);
        } else {
          console.warn(`Report API returned ${res.status}`);
        }
      } catch (err) {
        console.error("Failed to fetch report:", err);
      }
    }
  }, [stop, playDisconnectSound, webSocketAudioManager]);

  const handleToggleMic = useCallback(() => {
    setIsMicMuted((prev) => !prev);
    // TODO: Actually mute/unmute the microphone in webSocketAudioManager
  }, []);

  // Reset everything to initial state for a new call
  const handleNewCall = useCallback(() => {
    setSurveyReport(null);
    setActiveTab('guide');
    setConversationHistory([]);
    setCallDuration(0);
    setCallPhase('initial');
    backendSessionIdRef.current = null;
    setDisplayedCallId(null);
    setReportModalOpen(false);
    setReportText("");
    setReportStatus(null);
  }, []);

  const handleOpenReport = useCallback(() => {
    setReportText("");
    setReportStatus(null);
    setReportModalOpen(true);
  }, []);

  const handleCloseReport = useCallback(() => {
    if (reportSubmitting) return;
    setReportModalOpen(false);
  }, [reportSubmitting]);

  const handleSubmitReport = useCallback(async () => {
    const sessionId = displayedCallId;
    const content = reportText.trim();
    if (!sessionId || !content) return;

    const phaseText = getCallPhaseText();
    setReportSubmitting(true);
    setReportStatus(null);
    // Close the popup immediately on click (optimistic UX); the actual
    // success/error from the network is surfaced via the toast notification.
    // The inline report card on the report tab still reads `reportStatus`,
    // so users get persistent feedback there too.
    setReportModalOpen(false);
    try {
      const backendUrl = resolveBackendUrl();
      const res = await fetch(`${backendUrl}/v1/sessions/${sessionId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content,
          scenario: selectedScenario,
          use_case_id: selectedUseCaseId,
          tester_name: testerName,
        }),
      });
      if (res.ok) {
        setReportStatus({ kind: "success", msg: phaseText.reportSent });
        setNotificationState({ title: phaseText.reportSent, message: "" });
      } else {
        const body = await res.json().catch(() => null);
        const formatDetail = (d: unknown): string => {
          if (typeof d === 'string') return d;
          if (Array.isArray(d)) {
            return d
              .map((e) => (e && typeof e === 'object' && 'msg' in e ? String((e as { msg: unknown }).msg) : JSON.stringify(e)))
              .join('; ');
          }
          if (d && typeof d === 'object') return JSON.stringify(d);
          return '';
        };
        const detail = body && typeof body === 'object' ? formatDetail((body as { detail?: unknown }).detail) : '';
        const errMsg = detail || `${phaseText.reportFailed} (${res.status})`;
        setReportStatus({ kind: "error", msg: errMsg });
        setNotificationState({ title: phaseText.reportFailed, message: errMsg });
      }
    } catch (err) {
      const errMsg = `${phaseText.reportNetworkError}: ${(err as Error).message}`;
      setReportStatus({ kind: "error", msg: errMsg });
      setNotificationState({ title: phaseText.reportNetworkError, message: (err as Error).message });
    } finally {
      setReportSubmitting(false);
    }
    // getCallPhaseText is intentionally omitted: it is declared after this
    // callback. The body resolves it at invocation time via lexical scope; its
    // implementation reads `i18n.language` at call time, so messages always
    // reflect the current language even if the captured reference is stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedCallId, reportText, selectedScenario, selectedUseCaseId, testerName]);

  const renderInlineReport = useCallback(() => {
    if (!displayedCallId) return null;
    const phaseText = getCallPhaseText();
    const sent = reportStatus?.kind === "success";
    return (
      <InlineReportCard>
        <InlineReportTitle>{phaseText.reportTitle}</InlineReportTitle>
        {sent ? (
          <ModalStatus kind="success">{reportStatus.msg}</ModalStatus>
        ) : (
          <>
            <ModalTextarea
              placeholder={phaseText.reportPlaceholder}
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              disabled={reportSubmitting}
            />
            {reportStatus && (
              <ModalStatus kind={reportStatus.kind}>{reportStatus.msg}</ModalStatus>
            )}
            <ModalButtonRow>
              <ModalButton
                primary
                onClick={handleSubmitReport}
                disabled={reportSubmitting || !reportText.trim()}
              >
                {reportSubmitting ? phaseText.reportSubmitting : phaseText.reportSubmitInline}
              </ModalButton>
            </ModalButtonRow>
          </>
        )}
      </InlineReportCard>
    );
    // See note above on handleSubmitReport for why getCallPhaseText is omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedCallId, reportText, reportStatus, reportSubmitting, handleSubmitReport]);

  const formatCallDuration = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const renderTtsContent = useCallback(
    (content: string, isMobileMapView?: boolean) => (
      <TtsDisplayContainer isMobileMapView={isMobileMapView}>
        <TtsContent isMobileMapView={isMobileMapView}>
          <span
            ref={
              isMobileMapView
                ? ttsMobileContentElementRef
                : ttsContentElementRef
            }
          >
            {content}
          </span>
          {isTtsCharacterDisplaying && <span className="cursor"></span>}
        </TtsContent>
      </TtsDisplayContainer>
    ),
    [isTtsCharacterDisplaying]
  );

  const renderThinkingContent = useCallback((isMobileMapView?: boolean) => {
    return null;
  }, []);

  // Handle language change - send to backend via WebSocket
  const handleLanguageChange = useCallback(
    (languageCode: string) => {
      console.log(`[LANGUAGE_CHANGE] Sending language change to backend: ${languageCode}`);
      webSocketAudioManager.sendEvent("language_change", { language: languageCode });
      try {
        localStorage.setItem(LANGUAGE_CHOSEN_KEY, "1");
      } catch {
        // ignore storage failures (private mode, quota)
      }
      setHasChosenLanguage(true);
      setIsEditingLanguage(false);
    },
    [webSocketAudioManager]
  );

  // Scenario definitions with per-language cards
  const scenarioCards: Record<string, Record<string, Array<{ icon: string; title: string; description: string }>>> = useMemo(() => ({
    ha_scenario: {
      vi: [
        { icon: "user", title: "Kỹ thuật viên", description: "Kiểm tra đã đến và mặc đồng phục" },
        { icon: "star", title: "Điểm hài lòng", description: "Thu thập đánh giá từ 1-7 điểm" },
        { icon: "phone", title: "Kênh liên hệ", description: "Kiểm tra khách biết hotline" },
      ],
      en: [
        { icon: "user", title: "Technician", description: "Check arrival and uniform" },
        { icon: "star", title: "Satisfaction Score", description: "Collect rating from 1-7" },
        { icon: "phone", title: "Contact Channels", description: "Check hotline awareness" },
      ],
      km: [
        { icon: "user", title: "អ្នកបច្ចេកទេស", description: "ពិនិត្យការមកដល់និងឯកសណ្ឋាន" },
        { icon: "star", title: "ពិន្ទុការពេញចិត្ត", description: "ប្រមូលការវាយតម្លៃពី ១-៧" },
        { icon: "phone", title: "បណ្តាញទំនាក់ទំនង", description: "ពិនិត្យការដឹង hotline" },
      ],
    },
    technical_issue_scenario: {
      vi: [
        { icon: "tool", title: "Kỹ thuật viên kiểm tra", description: "KTV đã đến xử lý sự cố chưa" },
        { icon: "thunderbolt", title: "Phân loại sự cố", description: "Xác định loại sự cố kỹ thuật" },
        { icon: "phone", title: "Theo dõi sự cố", description: "Sự cố lặp lại hay mới" },
      ],
      en: [
        { icon: "tool", title: "Technician Visit", description: "Has technician come to check" },
        { icon: "thunderbolt", title: "Issue Classification", description: "Identify technical issue type" },
        { icon: "phone", title: "Issue Tracking", description: "Repeated or new issue" },
      ],
    },
    connection_scenario: {
      vi: [
        { icon: "wifi", title: "Tốc độ Internet", description: "Đánh giá chất lượng kết nối" },
        { icon: "user", title: "Thái độ nhân viên", description: "Đánh giá KTV lắp đặt" },
        { icon: "star", title: "Nghiệm thu & Hotline", description: "Kiểm tra TAM và hotline" },
      ],
      en: [
        { icon: "wifi", title: "Internet Speed", description: "Evaluate connection quality" },
        { icon: "user", title: "Staff Behavior", description: "Rate installation technician" },
        { icon: "star", title: "TAM & Hotline", description: "Check TAM document and hotline" },
      ],
    },
    wifi_survey_scenario: {
      vi: [
        { icon: "thunderbolt", title: "Vấn đề kết nối", description: "Khảo sát sự cố Wifi gặp phải" },
        { icon: "phone", title: "Phản ánh & Hotline", description: "Kiểm tra đã gọi phản ánh chưa" },
        { icon: "star", title: "Đánh giá dịch vụ", description: "Thu thập mức độ hài lòng" },
      ],
      en: [
        { icon: "thunderbolt", title: "Connection Issues", description: "Survey Wifi problems encountered" },
        { icon: "phone", title: "Complaint & Hotline", description: "Check complaint history" },
        { icon: "star", title: "Service Rating", description: "Collect satisfaction level" },
      ],
    },
  }), []);

  // Scenario selector labels
  const scenarioLabels = useMemo(() => ({
    vi: {
      label: "Lựa chọn kịch bản khảo sát",
      scenarioLabel: "Kịch bản",
      scenarios: {
        ha_scenario: "Khảo sát hài lòng",
        technical_issue_scenario: "Sự cố kỹ thuật",
        connection_scenario: "Lắp đặt mới",
        wifi_survey_scenario: "Khảo sát Wifi",
      } as Record<string, string>,
    },
    en: {
      label: "Select survey scenario",
      scenarioLabel: "Scenario",
      scenarios: {
        ha_scenario: "Satisfaction Survey",
        technical_issue_scenario: "Technical Issue",
        connection_scenario: "New Connection",
        wifi_survey_scenario: "Wifi Survey",
      } as Record<string, string>,
    },
    km: {
      label: "ជ្រើសរើសកម្មវិធីស្ទង់មតិ",
      scenarioLabel: "កម្មវិធី",
      scenarios: {
        ha_scenario: "ស្ទង់មតិពេញចិត្ត",
        technical_issue_scenario: "បញ្ហាបច្ចេកទេស",
        connection_scenario: "ការតភ្ជាប់ថ្មី",
        wifi_survey_scenario: "ស្ទង់មតិ Wifi",
      } as Record<string, string>,
    },
  } as Record<string, { label: string; scenarioLabel: string; scenarios: Record<string, string> }>), []);

  // Scenario icon mapping
  const scenarioIcons: Record<string, React.ReactNode> = useMemo(() => ({
    ha_scenario: <StarOutlined />,
    technical_issue_scenario: <ToolOutlined />,
    connection_scenario: <LinkOutlined />,
    wifi_survey_scenario: <WifiOutlined />,
  }), []);

  // Translations for call phases
  const getCallPhaseText = useCallback(() => {
    const lang = i18n.language;
    const scenarioCardSet = scenarioCards[selectedScenario]?.[lang] || scenarioCards[selectedScenario]?.vi || scenarioCards.ha_scenario.vi;
    const texts: Record<string, {
      title: string;
      subtitle: string;
      infoTitle: string;
      infoText: string;
      cards: Array<{ icon: string; title: string; description: string }>;
      languageLabel: string;
      changeLanguage: string;
      begin: string;
      beginFreestyle: string;
      ringing: string;
      answer: string;
      newCall: string;
      useCaseLabel: string;
      useCaseLoading: string;
      noUseCases: string;
      selectUseCase: string;
      reloadFromSheet: string;
      sttModelLabel: string;
      sttModelV1: string;
      sttModelHint: string;
      connecting: string;
      pleaseSelectUseCase: string;
      share: string;
      shareTitle: string;
      tabGuide: string;
      tabConversation: string;
      tabReport: string;
      emptyGuide: string;
      loadingReport: string;
      pleaseRetry: string;
      cannotConnect: string;
      reportTitle: string;
      reportPlaceholder: string;
      reportCancel: string;
      reportSubmit: string;
      reportSubmitInline: string;
      reportSubmitting: string;
      reportSent: string;
      reportFailed: string;
      reportNetworkError: string;
      newConversation: string;
    }> = {
      vi: {
        title: "Contact AP Outbound",
        subtitle: "Hệ thống AI Agent khảo sát khách hàng Metfone",
        infoTitle: "Về cuộc gọi này",
        infoText: "Hệ thống AI Agent tự động thực hiện khảo sát đánh giá chất lượng dịch vụ Internet và hỗ trợ kỹ thuật của Metfone.",
        cards: scenarioCardSet,
        languageLabel: "Lựa chọn ngôn ngữ tương tác",
        changeLanguage: "Thay đổi",
        begin: "Bắt đầu",
        beginFreestyle: "Bắt đầu (tự do)",
        ringing: "Đang gọi đến...",
        answer: "Nghe máy",
        newCall: "Tạo cuộc mới",
        useCaseLabel: "Use Case",
        useCaseLoading: "Đang tải...",
        noUseCases: "(không có use case)",
        selectUseCase: "-- Chọn use case --",
        reloadFromSheet: "Tải lại từ Google Sheet",
        sttModelLabel: "Model STT Khmer",
        sttModelV1: "Model 1 (mặc định)",
        sttModelHint: "Áp dụng cho cuộc gọi tiếp theo",
        connecting: "Đang kết nối...",
        pleaseSelectUseCase: "Vui lòng chọn use case",
        share: "Chia sẻ",
        shareTitle: "Sao chép link chia sẻ",
        tabGuide: "Hướng dẫn",
        tabConversation: "Hội thoại",
        tabReport: "Báo cáo khảo sát",
        emptyGuide: "Chưa có hướng dẫn cho use case này.",
        loadingReport: "Đang tải báo cáo khảo sát...",
        pleaseRetry: "Vui lòng thử lại.",
        cannotConnect: "Không thể kết nối đến máy chủ. Vui lòng thử lại.",
        reportTitle: "Gửi report cuộc gọi",
        reportPlaceholder: "Nội dung report (mô tả vấn đề, ghi chú, v.v.)",
        reportCancel: "Hủy",
        reportSubmit: "Gửi",
        reportSubmitInline: "Gửi report",
        reportSubmitting: "Đang gửi...",
        reportSent: "Đã gửi report thành công",
        reportFailed: "Gửi thất bại",
        reportNetworkError: "Lỗi mạng",
        newConversation: "Bắt đầu cuộc hội thoại mới",
      },
      en: {
        title: "Contact AP Outbound",
        subtitle: "Metfone Customer AI Agent Survey System",
        infoTitle: "About this call",
        infoText: "AI Agent system automatically conducts surveys to evaluate Metfone's Internet service quality and technical support.",
        cards: scenarioCardSet,
        languageLabel: "Select interaction language",
        changeLanguage: "Change",
        begin: "Begin",
        beginFreestyle: "Begin (freestyle)",
        ringing: "Incoming call...",
        answer: "Answer",
        newCall: "New Call",
        useCaseLabel: "Use Case",
        useCaseLoading: "Loading...",
        noUseCases: "(no use cases)",
        selectUseCase: "-- Select a use case --",
        reloadFromSheet: "Reload from Google Sheet",
        sttModelLabel: "Khmer STT model",
        sttModelV1: "Model 1 (default)",
        sttModelHint: "Applies on the next call",
        connecting: "Connecting...",
        pleaseSelectUseCase: "Please select a use case",
        share: "Share",
        shareTitle: "Copy share link",
        tabGuide: "Guide",
        tabConversation: "Conversation",
        tabReport: "Survey Report",
        emptyGuide: "No guide available for this use case.",
        loadingReport: "Loading survey report...",
        pleaseRetry: "Please try again.",
        cannotConnect: "Unable to reach the server. Please try again.",
        reportTitle: "Submit call report",
        reportPlaceholder: "Report content (describe issue, notes, etc.)",
        reportCancel: "Cancel",
        reportSubmit: "Submit",
        reportSubmitInline: "Submit report",
        reportSubmitting: "Submitting...",
        reportSent: "Report submitted successfully",
        reportFailed: "Submission failed",
        reportNetworkError: "Network error",
        newConversation: "Start new conversation",
      },
      km: {
        title: "Contact AP Outbound",
        subtitle: "ប្រព័ន្ធ AI Agent ស្ទង់មតិអតិថិជន Metfone",
        infoTitle: "អំពីការហៅនេះ",
        infoText: "ប្រព័ន្ធ AI Agent ធ្វើការស្ទង់មតិដោយស្វ័យប្រវត្តិ ដើម្បីវាយតម្លៃគុណភាពសេវាអ៊ីនធឺណិត និងជំនួយបច្ចេកទេសរបស់ Metfone។",
        cards: scenarioCardSet,
        languageLabel: "ជ្រើសរើសភាសាសម្រាប់អន្តរកម្ម",
        changeLanguage: "ផ្លាស់ប្តូរ",
        begin: "ចាប់ផ្តើម",
        beginFreestyle: "ចាប់ផ្តើម (freestyle)",
        ringing: "កំពុងហៅមក...",
        answer: "ឆ្លើយ",
        newCall: "ការហៅថ្មី",
        useCaseLabel: "Use Case",
        useCaseLoading: "កំពុងផ្ទុក...",
        noUseCases: "(គ្មាន use case)",
        selectUseCase: "-- ជ្រើសរើស use case --",
        reloadFromSheet: "ផ្ទុកឡើងវិញពី Google Sheet",
        sttModelLabel: "Model STT ខ្មែរ",
        sttModelV1: "Model 1 (លំនាំដើម)",
        sttModelHint: "អនុវត្តសម្រាប់ការហៅបន្ទាប់",
        connecting: "កំពុងភ្ជាប់...",
        pleaseSelectUseCase: "សូមជ្រើសរើស use case",
        share: "ចែករំលែក",
        shareTitle: "ចម្លងតំណចែករំលែក",
        tabGuide: "មគ្គុទ្ទេសក៍",
        tabConversation: "ការសន្ទនា",
        tabReport: "របាយការណ៍ស្ទង់មតិ",
        emptyGuide: "មិនទាន់មានមគ្គុទ្ទេសក៍សម្រាប់ use case នេះទេ។",
        loadingReport: "កំពុងផ្ទុករបាយការណ៍ស្ទង់មតិ...",
        pleaseRetry: "សូមព្យាយាមម្តងទៀត។",
        cannotConnect: "មិនអាចភ្ជាប់ទៅម៉ាស៊ីនមេបានទេ។ សូមព្យាយាមម្តងទៀត។",
        reportTitle: "ផ្ញើរបាយការណ៍ការហៅ",
        reportPlaceholder: "ខ្លឹមសាររបាយការណ៍ (ពណ៌នាបញ្ហា កំណត់ចំណាំ ។ល។)",
        reportCancel: "បោះបង់",
        reportSubmit: "ផ្ញើ",
        reportSubmitInline: "ផ្ញើរបាយការណ៍",
        reportSubmitting: "កំពុងផ្ញើ...",
        reportSent: "បានផ្ញើរបាយការណ៍ដោយជោគជ័យ",
        reportFailed: "ផ្ញើបរាជ័យ",
        reportNetworkError: "កំហុសបណ្តាញ",
        newConversation: "ចាប់ផ្តើមការសន្ទនាថ្មី",
      },
    };
    return texts[lang] || texts.vi;
  }, [i18n.language, selectedScenario, scenarioCards]);

  // Icon mapping helper
  const renderCardIcon = useCallback((iconName: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      wifi: <WifiOutlined />,
      user: <UserOutlined />,
      star: <StarOutlined />,
      phone: <PhoneOutlined />,
      info: <InfoCircleOutlined />,
      tool: <ToolOutlined />,
      thunderbolt: <ThunderboltOutlined />,
      link: <LinkOutlined />,
    };
    return iconMap[iconName] || null;
  }, []);

  // Card colors for each index
  const cardColors = [
    { bg: 'rgba(160, 176, 246, 0.376)', icon: '#A0B0F6' },
    { bg: 'rgba(187, 171, 237, 0.376)', icon: '#BBABED' },
    { bg: 'rgba(210, 167, 233, 0.376)', icon: '#D2A7E9' },
    { bg: 'rgba(255, 181, 214, 0.376)', icon: '#FFB5D6' },
  ];

  // Render initial fullscreen
  const renderInitialScreen = useCallback(() => {
    const phaseText = getCallPhaseText();
    const languageFlags: Record<string, string> = { en: "🇬🇧", vi: "🇻🇳", km: "🇰🇭" };
    const showCompactLanguage = hasChosenLanguage && !isEditingLanguage;
    const currentFlag = languageFlags[i18n.language] || languageFlags.km;
    return (
      <FullScreenInitial>
        {!isShared && (
          <LanguageSelectorContainer>
            {showCompactLanguage ? (
              <LanguagePillRow>
                <LanguagePillFlag>{currentFlag}</LanguagePillFlag>
                <LanguagePillChange type="button" onClick={() => setIsEditingLanguage(true)}>
                  {phaseText.changeLanguage}
                </LanguagePillChange>
              </LanguagePillRow>
            ) : (
              <>
                <LanguageLabel>{phaseText.languageLabel}</LanguageLabel>
                <LanguageSelector onLanguageChange={handleLanguageChange} />
              </>
            )}
          </LanguageSelectorContainer>
        )}

        {!sharedAutoFlow && (
          <>
            <ScenarioSelectorContainer>
              <ScenarioLabel>{(scenarioLabels[i18n.language] || scenarioLabels.vi).label}</ScenarioLabel>
              <ScenarioButtonGroup>
                {Object.entries((scenarioLabels[i18n.language] || scenarioLabels.vi).scenarios).map(([key, label]) => (
                  <ScenarioButton
                    key={key}
                    isActive={selectedScenario === key}
                    onClick={() => setSelectedScenario(key)}
                  >
                    {scenarioIcons[key]}
                    <span>{label}</span>
                  </ScenarioButton>
                ))}
              </ScenarioButtonGroup>
            </ScenarioSelectorContainer>

            <ScenarioSelectorContainer>
              <ScenarioLabel>{phaseText.useCaseLabel}</ScenarioLabel>
              <UseCaseRow>
                <UseCaseSelect
                  value={selectedUseCaseId}
                  disabled={useCasesLoading || useCases.length === 0}
                  onChange={(e) => setSelectedUseCaseId(e.target.value)}
                >
                  <option value="">
                    {useCasesLoading
                      ? phaseText.useCaseLoading
                      : useCases.length === 0
                        ? phaseText.noUseCases
                        : phaseText.selectUseCase}
                  </option>
                  {useCases.map((uc) => (
                    <option key={uc.id} value={uc.id}>
                      {uc.description ? `${uc.id} — ${uc.description}` : uc.id}
                    </option>
                  ))}
                </UseCaseSelect>
                <RefreshButton
                  type="button"
                  onClick={() => reloadUseCases(selectedScenario)}
                  disabled={useCasesLoading}
                  title={phaseText.reloadFromSheet}
                >
                  {useCasesLoading ? "..." : "↻"}
                </RefreshButton>
              </UseCaseRow>
              {useCasesError && <UseCaseHint isError>{useCasesError}</UseCaseHint>}
              {useCaseGuide && (
                <InlineUseCaseDetail>
                  <UseCaseDetailPanel>
                    <UseCaseGuideMarkdown>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {useCaseGuide}
                      </ReactMarkdown>
                    </UseCaseGuideMarkdown>
                  </UseCaseDetailPanel>
                </InlineUseCaseDetail>
              )}
            </ScenarioSelectorContainer>

            <ScenarioSelectorContainer>
              <ScenarioLabel>{phaseText.sttModelLabel}</ScenarioLabel>
              <ScenarioButtonGroup>
                {STT_MODEL_OPTIONS.map((opt) => (
                  <ScenarioButton
                    key={opt.value}
                    isActive={normalizeSttModel(sttModel) === opt.value}
                    onClick={() => setSttModel(opt.value)}
                  >
                    {/* Only the default gets a localized label ("Model 1
                        (default)"); the rest are checkpoint names. */}
                    <span>
                      {opt.value === DEFAULT_STT_MODEL
                        ? phaseText.sttModelV1
                        : opt.label}
                    </span>
                  </ScenarioButton>
                ))}
              </ScenarioButtonGroup>
              <UseCaseHint>{phaseText.sttModelHint}</UseCaseHint>
            </ScenarioSelectorContainer>
          </>
        )}

        <BeginButton
          onClick={handleBegin}
          disabled={isConnecting || (!sharedAutoFlow && useCases.length > 0 && !selectedUseCaseId)}
        >
          {isConnecting
            ? phaseText.connecting
            : !sharedAutoFlow && useCases.length > 0 && !selectedUseCaseId
              ? phaseText.pleaseSelectUseCase
              : useCases.length === 0
                ? phaseText.beginFreestyle
                : phaseText.begin}
        </BeginButton>

        {!isShared && (
          <InitialShareButton
            type="button"
            onClick={handleShare}
            title={phaseText.shareTitle}
          >
            <ShareAltOutlined />
            <span>{phaseText.share}</span>
          </InitialShareButton>
        )}

        {!isShared && <InitialTitle>{phaseText.title}</InitialTitle>}
        {!isShared && <InitialSubtitle>{phaseText.infoText}</InitialSubtitle>}

        {!isShared && (
        <CardsWrapper hasHiddenCards={!isCardsExpanded && visibleCardCount < phaseText.cards.length}>
          <CardsContainer>
            {phaseText.cards.map((card, index) => (
              <MobileCardWrapper key={index} index={index} isExpanded={isCardsExpanded} visibleCount={visibleCardCount}>
                <PurposeCard style={{ backgroundColor: cardColors[index].bg }}>
                  <CardIcon style={{ background: 'rgba(255, 255, 255, 0.7)' }}>
                    <span style={{ color: cardColors[index].icon }}>{renderCardIcon(card.icon)}</span>
                  </CardIcon>
                  <CardContent>
                    <CardTitle>{card.title}</CardTitle>
                    <CardDescription>{card.description}</CardDescription>
                  </CardContent>
                </PurposeCard>
              </MobileCardWrapper>
            ))}
          </CardsContainer>
          {/* Only show expand button when there are hidden cards */}
          {!isCardsExpanded && visibleCardCount < phaseText.cards.length && (
            <ExpandIconButton onClick={() => setIsCardsExpanded(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 6 12 12 18 6"></polyline>
                <polyline points="6 13 12 19 18 13"></polyline>
              </svg>
            </ExpandIconButton>
          )}
          {isCardsExpanded && visibleCardCount < phaseText.cards.length && (
            <ExpandIconButton onClick={() => setIsCardsExpanded(false)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="18 18 12 12 6 18"></polyline>
                <polyline points="18 11 12 5 6 11"></polyline>
              </svg>
            </ExpandIconButton>
          )}
        </CardsWrapper>
        )}
      </FullScreenInitial>
    );
  }, [getCallPhaseText, handleBegin, handleLanguageChange, renderCardIcon, isCardsExpanded, visibleCardCount, isConnecting, selectedScenario, scenarioLabels, scenarioIcons, i18n.language, selectedUseCaseId, useCases, useCasesLoading, useCasesError, useCaseGuide, reloadUseCases, hasChosenLanguage, isEditingLanguage, isShared, sharedAutoFlow, handleShare, sttModel, setSttModel]);

  // Render ringing fullscreen
  const renderRingingScreen = useCallback(() => {
    const phaseText = getCallPhaseText();
    return (
      <RingingScreen>
        <RingingText>{phaseText.ringing}</RingingText>
        <CallerName>Sale Agent</CallerName>
        <RingingOrbWrapper>
          <RingingRing size={200} delay={0} />
          <RingingRing size={160} delay={1} />
          <RingingRing size={125} delay={2} />
          <RingingOrbCenter>
            <PhoneOutlined />
          </RingingOrbCenter>
        </RingingOrbWrapper>
        <CallerPhone>033.333.333</CallerPhone>
        <AnswerButton onClick={handleAnswer}>
          {phaseText.answer}
          <PhoneOutlined />
        </AnswerButton>
      </RingingScreen>
    );
  }, [getCallPhaseText, handleAnswer]);

  const renderOrb = useCallback(
    (isMapVisibleOnMobile?: boolean) => {
      const isActive = isListening || isSpeaking || isThinking;
      const phaseText = getCallPhaseText();

      // Active call screen
      return (
        <>
          <OrbContainer isMapVisibleOnMobile={isMapVisibleOnMobile}>
            <OrbWrapper>
              {/* 2 Outer Rings + Main Orb = 3 layers, khoảng cách đều 16px */}
              <Ring size={144} delay={0} isActive={isActive} audioLevel={audioLevel} />
              <Ring size={112} delay={1} isActive={isActive} audioLevel={audioLevel} />
              {/* Main Orb */}
              <PulsingOrb isActive={isActive} audioLevel={audioLevel}>
                <OrbInner isActive={isActive} audioLevel={audioLevel} />
              </PulsingOrb>
            </OrbWrapper>
          </OrbContainer>
          <ControlsContainer>
            {callPhase === 'ended' && renderInlineReport()}
            <ControlsCard>
              {isMobile && (
                <MiniOrbContainer>
                  <MiniOrbRing size={40} delay={0} isActive={isActive} audioLevel={audioLevel} />
                  <MiniOrbRing size={32} delay={1} isActive={isActive} audioLevel={audioLevel} />
                  <MiniOrbCenter isActive={isActive} audioLevel={audioLevel} />
                </MiniOrbContainer>
              )}
              <SpeakerInfo>
                <SpeakerNameRow>
                  <SpeakerName>SaleAgent</SpeakerName>
                </SpeakerNameRow>
                <Timer>{formatCallDuration(callDuration)}</Timer>
                {displayedCallId && <CallId>ID: {displayedCallId}</CallId>}
                {testerName && (
                  <TesterBadge onClick={handleEditTesterName} title="Đổi tên người test">
                    👤 {testerName}
                  </TesterBadge>
                )}
              </SpeakerInfo>
              <ButtonGroup>
                {callPhase === 'ended' ? (
                  <>
                    <NewConversationButton
                      onClick={handleNewCall}
                      aria-label={phaseText.newConversation}
                    >
                      <PhoneOutlined />
                      {phaseText.newConversation}
                    </NewConversationButton>
                  </>
                ) : isStart ? (
                  <>
                    <ControlButton
                      onClick={() => setGuidelineModalOpen(true)}
                      aria-label="Xem kịch bản"
                      title="Xem kịch bản"
                    >
                      <BookOutlined />
                    </ControlButton>
                    <ControlButton
                      onClick={handleToggleMic}
                      aria-label={isMicMuted ? "Unmute" : "Mute"}
                    >
                      {isMicMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
                    </ControlButton>
                    <ControlButton variant="danger" onClick={handleHangUp} aria-label="Hang up">
                      <PhoneOutlined style={{ transform: "rotate(135deg)" }} />
                    </ControlButton>
                  </>
                ) : (
                  <ControlButton
                    variant="primary"
                    onClick={handleCallStart}
                    aria-label="Start call"
                    style={{ background: "rgb(187, 171, 237)", color: "#fff" }}
                  >
                    <PhoneOutlined />
                  </ControlButton>
                )}
              </ButtonGroup>
            </ControlsCard>
          </ControlsContainer>
        </>
      );
    },
    [isListening, isThinking, isSpeaking, isMobile, audioLevel, isStart, callDuration, isMicMuted, formatCallDuration, handleCallStart, handleHangUp, handleToggleMic, callPhase, handleNewCall, displayedCallId, renderInlineReport, testerName, handleEditTesterName, getCallPhaseText]
  );

  // Render mobile controls (for RightSection on mobile)
  const renderMobileControls = useCallback(() => {
    const isActive = isListening || isSpeaking || isThinking;
    const phaseText = getCallPhaseText();
    return (
      <MobileControlsStack>
        {callPhase === 'ended' && renderInlineReport()}
        <ControlsCard>
        <MiniOrbContainer>
          <MiniOrbRing size={40} delay={0} isActive={isActive} audioLevel={audioLevel} />
          <MiniOrbRing size={32} delay={1} isActive={isActive} audioLevel={audioLevel} />
          <MiniOrbCenter isActive={isActive} audioLevel={audioLevel} />
        </MiniOrbContainer>
        <SpeakerInfo>
          <SpeakerNameRow>
            <SpeakerName>SaleAgent</SpeakerName>
          </SpeakerNameRow>
          <Timer>{formatCallDuration(callDuration)}</Timer>
          {displayedCallId && <CallId>ID: {displayedCallId}</CallId>}
        </SpeakerInfo>
        <ButtonGroup>
          {callPhase === 'ended' ? (
            <>
              <NewConversationButton
                onClick={handleNewCall}
                aria-label={phaseText.newConversation}
              >
                <PhoneOutlined />
                {phaseText.newConversation}
              </NewConversationButton>
            </>
          ) : isStart ? (
            <>
              <ControlButton
                onClick={() => setGuidelineModalOpen(true)}
                aria-label="Xem kịch bản"
                title="Xem kịch bản"
              >
                <BookOutlined />
              </ControlButton>
              <ControlButton
                onClick={handleToggleMic}
                aria-label={isMicMuted ? "Unmute" : "Mute"}
              >
                {isMicMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
              </ControlButton>
              <ControlButton variant="danger" onClick={handleHangUp} aria-label="Hang up">
                <PhoneOutlined style={{ transform: "rotate(135deg)" }} />
              </ControlButton>
            </>
          ) : (
            <ControlButton
              variant="primary"
              onClick={handleCallStart}
              aria-label="Start call"
              style={{ background: "rgb(187, 171, 237)", color: "#fff" }}
            >
              <PhoneOutlined />
            </ControlButton>
          )}
        </ButtonGroup>
      </ControlsCard>
      </MobileControlsStack>
    );
  }, [isListening, isThinking, isSpeaking, isStart, callDuration, isMicMuted, audioLevel, formatCallDuration, handleCallStart, handleHangUp, handleToggleMic, callPhase, handleNewCall, displayedCallId, renderInlineReport, testerName, handleEditTesterName, getCallPhaseText]);

  const handleNewsPlayPause = useCallback(() => {
    if (newsManager.isNewsPaused) {
      newsManager.handleResumeNews();
    } else {
      newsManager.handlePauseNews();
    }
  }, [newsManager]);

  const handleNextNewsItem = useCallback(() => {
    newsManager.handleNextNewsItem();

    // Send news_next_completed event
    updateToolState(WS_EVENTS.NEWS_NEXT_COMPLETED);
  }, [newsManager, webSocketAudioManager]);

  const connectionNotification = useMemo(
    () => (
      <ConnectionNotification
        isConnecting={isConnecting}
        isComplete={isConnectionComplete}
        onHide={() => {
          setIsConnecting(false);
          setIsConnectionComplete(false);
        }}
      />
    ),
    [isConnecting, isConnectionComplete]
  );

  const newsProps = useMemo(
    () => ({
      currentNewsTitle,
      newsDuration,
      fullContent,
      isPlayingNews: newsManager.isPlayingNews || newsManager.isPlayingSummary,
      isNewsPaused: newsManager.isNewsPaused,
      isMuted: newsManager.isMuted,
      getCurrentAudioTime: newsManager.getCurrentAudioTime,
      onPlayPause: handleNewsPlayPause,
      onToggleMute: newsManager.handleToggleMute,
      onNext: handleNextNewsItem,
      onPrev: newsManager.handlePrevNewsItem,
      isPlayingOnMap: isNewsPlayingOnMap,
    }),
    [
      currentNewsTitle,
      newsDuration,
      fullContent,
      newsManager.isPlayingNews,
      newsManager.isPlayingSummary,
      newsManager.isNewsPaused,
      newsManager.isMuted,
      newsManager.getCurrentAudioTime,
      handleNewsPlayPause,
      newsManager.handleToggleMute,
      handleNextNewsItem,
      newsManager.handlePrevNewsItem,
      isNewsPlayingOnMap,
    ]
  );

  // const isNewsPlayingOnMap =
  //   selectedTool === "map" &&
  //   (newsManager.isPlayingNews || newsManager.isPlayingSummary);
  const shouldShowOrb = !(isMobile && isMapVisible);
  const mobileBarIsDisplaying =
    isThinking ||
    !!(isNewsPlayingOnMap && currentNewsTitle);

  const renderLeftSectionContent = () => {
    if (isMobile) {
      if (isThinking) {
        return renderThinkingContent();
      }
      return null;
    }

    if (isThinking) {
      return renderThinkingContent();
    }
    if (isNewsPlayingOnMap && isMapVisible) {
      return <News {...newsProps} />;
    }
    // Don't show TTS content here - only show in conversation history
    return null;
  };

  // Helper function to get report status
  const getReportStatus = (value: any): 'answered' | 'skipped' | 'pending' => {
    if (value === null || value === undefined || value === '') return 'pending';
    if (value === 'Bỏ qua' || value === 'Skipped') return 'skipped';
    return 'answered';
  };

  // Render survey report content
  const renderSurveyReport = () => {
    const phaseText = getCallPhaseText();
    if (!surveyReport) {
      return (
        <EmptyReport>
          <span>
            {isWaitingForSurveyReport
              ? phaseText.loadingReport
              : phaseText.tabReport}
          </span>
        </EmptyReport>
      );
    }

    const getBadgeLabel = (status: 'answered' | 'skipped' | 'pending') => {
      switch (status) {
        case 'answered': return 'Đã trả lời';
        case 'skipped': return 'Bỏ qua';
        default: return 'Chưa hỏi';
      }
    };

    const formatInternetStatus = (status: string | null) => {
      if (!status) return '-';
      const map: Record<string, string> = {
        'internet_ok': 'Ổn định',
        'internet_not_ok': 'Chưa ổn định',
        'unstable': 'Không ổn định',
        'ok': 'Ổn định'
      };
      return map[status] || status;
    };

    const formatUniform = (status: string | null) => {
      if (!status) return '-';
      const map: Record<string, string> = {
        'wear': 'Có mặc đồng phục',
        'no_wear': 'Không mặc đồng phục',
        'not_applicable': 'Không áp dụng'
      };
      return map[status] || status;
    };

    const formatHotline = (status: string | null) => {
      if (!status) return '-';
      const map: Record<string, string> = {
        'know': 'Đã biết',
        'not_know': 'Chưa biết'
      };
      return map[status] || status;
    };

    const formatChannel = (channel: string | null) => {
      if (!channel) return '-';
      const map: Record<string, string> = {
        'hotline': 'Hotline',
        'technical': 'Kỹ thuật viên',
        'sale_staff': 'Nhân viên bán hàng',
        'payment_staff': 'Nhân viên thu ngân',
        'showroom': 'Showroom',
        'other': 'Khác'
      };
      return map[channel] || channel;
    };

    const formatSatisfaction = (level: string | null) => {
      if (!level) return '';
      const map: Record<string, string> = {
        'satisfied': 'Hài lòng',
        'normal': 'Bình thường',
        'not_satisfied': 'Chưa hài lòng',
        'not_applicable': 'Không áp dụng'
      };
      return map[level] || level;
    };

    const completionPercentage = surveyReport.completion_percentage || 0;
    const missingQuestions = surveyReport.missing_questions || [];

    return (
      <ReportContainer>
        <ReportHeader>
          <ReportTitle>
            <ReportTitleText>Kết quả khảo sát</ReportTitleText>
            <ReportSubtitle>
              {surveyReport.duration_seconds
                ? `Thời lượng: ${Math.floor(surveyReport.duration_seconds / 60)}:${String(surveyReport.duration_seconds % 60).padStart(2, '0')}`
                : 'Đang xử lý...'}
            </ReportSubtitle>
          </ReportTitle>
          <ReportStatusContainer>
            <ReportStatus isComplete={surveyReport.is_complete}>
              {surveyReport.is_complete ? 'Hoàn thành' : `${completionPercentage}% hoàn thành`}
            </ReportStatus>
            {missingQuestions.length > 0 && (
              <MissingQuestions>
                {missingQuestions.map((q: string) => (
                  <MissingTag key={q}>{q}</MissingTag>
                ))}
              </MissingQuestions>
            )}
          </ReportStatusContainer>
        </ReportHeader>

        <ReportTable>
          <ReportThead>
            <tr>
              <th style={{ width: '22%' }}>Câu hỏi</th>
              <th style={{ width: '30%' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: '#4f46e5' }}>👤</span> Khách trả lời
                </span>
              </th>
              <th style={{ width: '28%' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: '#16a34a' }}>🤖</span> Bot phân tích
                </span>
              </th>
              <th style={{ width: '20%' }}>Trạng thái</th>
            </tr>
          </ReportThead>
          <ReportTbody>
            <tr>
              <td>
                <QuestionLabel>
                  <QuestionText>Q1: Tình trạng Internet</QuestionText>
                </QuestionLabel>
              </td>
              <td>
                <AnswerCell>
                  {surveyReport.q1_answer_text ? (
                    <AnswerSubtext style={{ color: '#374151', fontSize: 13, fontStyle: 'italic' }}>"{surveyReport.q1_answer_text}"</AnswerSubtext>
                  ) : (
                    <AnswerSubtext>-</AnswerSubtext>
                  )}
                </AnswerCell>
              </td>
              <td>
                <AnswerCell>
                  <AnswerValue>{formatInternetStatus(surveyReport.q1_internet_status)}</AnswerValue>
                </AnswerCell>
              </td>
              <td>
                <ReportBadge type={getReportStatus(surveyReport.q1_internet_status)}>
                  {getBadgeLabel(getReportStatus(surveyReport.q1_internet_status))}
                </ReportBadge>
              </td>
            </tr>
            <tr>
              <td>
                <QuestionLabel>
                  <QuestionText>Q2: KTV đến</QuestionText>
                </QuestionLabel>
              </td>
              <td>
                <AnswerCell>
                  {surveyReport.q2_answer_text ? (
                    <AnswerSubtext style={{ color: '#374151', fontSize: 13, fontStyle: 'italic' }}>"{surveyReport.q2_answer_text}"</AnswerSubtext>
                  ) : (
                    <AnswerSubtext>-</AnswerSubtext>
                  )}
                </AnswerCell>
              </td>
              <td>
                <AnswerCell>
                  <AnswerValue>
                    {surveyReport.q2_tech_visited === true ? 'Có' :
                     surveyReport.q2_tech_visited === false ? 'Không' : '-'}
                  </AnswerValue>
                </AnswerCell>
              </td>
              <td>
                <ReportBadge type={getReportStatus(surveyReport.q2_tech_visited)}>
                  {getBadgeLabel(getReportStatus(surveyReport.q2_tech_visited))}
                </ReportBadge>
              </td>
            </tr>
            <tr>
              <td>
                <QuestionLabel>
                  <QuestionText>Q3: Đồng phục</QuestionText>
                </QuestionLabel>
              </td>
              <td>
                <AnswerCell>
                  {surveyReport.q3_answer_text && surveyReport.q3_uniform !== 'not_applicable' ? (
                    <AnswerSubtext style={{ color: '#374151', fontSize: 13, fontStyle: 'italic' }}>"{surveyReport.q3_answer_text}"</AnswerSubtext>
                  ) : (
                    <AnswerSubtext>-</AnswerSubtext>
                  )}
                </AnswerCell>
              </td>
              <td>
                <AnswerCell>
                  <AnswerValue>{formatUniform(surveyReport.q3_uniform)}</AnswerValue>
                </AnswerCell>
              </td>
              <td>
                <ReportBadge type={surveyReport.q3_uniform === 'not_applicable' ? 'skipped' : getReportStatus(surveyReport.q3_uniform)}>
                  {surveyReport.q3_uniform === 'not_applicable' ? 'Không áp dụng' : getBadgeLabel(getReportStatus(surveyReport.q3_uniform))}
                </ReportBadge>
              </td>
            </tr>
            <tr>
              <td>
                <QuestionLabel>
                  <QuestionText>Q4: Điểm hài lòng</QuestionText>
                </QuestionLabel>
              </td>
              <td>
                <AnswerCell>
                  {surveyReport.q4_answer_text ? (
                    <AnswerSubtext style={{ color: '#374151', fontSize: 13, fontStyle: 'italic' }}>"{surveyReport.q4_answer_text}"</AnswerSubtext>
                  ) : (
                    <AnswerSubtext>-</AnswerSubtext>
                  )}
                </AnswerCell>
              </td>
              <td>
                <ScoreCell score={surveyReport.q4_score}>
                  <ScoreValue score={surveyReport.q4_score}>
                    {surveyReport.q4_score ? surveyReport.q4_score : '-'}
                  </ScoreValue>
                  {surveyReport.q4_score && (
                    <>
                      <ScoreBar score={surveyReport.q4_score} />
                      <ScoreLabel>/7 - {formatSatisfaction(surveyReport.q4_satisfaction_level)}</ScoreLabel>
                    </>
                  )}
                </ScoreCell>
              </td>
              <td>
                <ReportBadge type={surveyReport.q4_satisfaction_level === 'not_applicable' ? 'skipped' : getReportStatus(surveyReport.q4_score)}>
                  {surveyReport.q4_satisfaction_level === 'not_applicable' ? 'Không áp dụng' : getBadgeLabel(getReportStatus(surveyReport.q4_score))}
                </ReportBadge>
              </td>
            </tr>
            {(surveyReport.q4_reason || surveyReport.q4b_reason) && (
              <tr>
                <td>
                  <QuestionLabel>
                    <QuestionText>Q4b: Lý do</QuestionText>
                  </QuestionLabel>
                </td>
                <td>
                  <AnswerCell>
                    <AnswerSubtext style={{ color: '#374151', fontSize: 13, fontStyle: 'italic' }}>"{surveyReport.q4_reason || surveyReport.q4b_reason}"</AnswerSubtext>
                  </AnswerCell>
                </td>
                <td>
                  <AnswerCell>
                    <AnswerValue>{surveyReport.q4_reason || surveyReport.q4b_reason}</AnswerValue>
                  </AnswerCell>
                </td>
                <td><ReportBadge type="answered">Đã trả lời</ReportBadge></td>
              </tr>
            )}
            <tr>
              <td>
                <QuestionLabel>
                  <QuestionText>Q5: Biết hotline</QuestionText>
                </QuestionLabel>
              </td>
              <td>
                <AnswerCell>
                  {surveyReport.q5_answer_text ? (
                    <AnswerSubtext style={{ color: '#374151', fontSize: 13, fontStyle: 'italic' }}>"{surveyReport.q5_answer_text}"</AnswerSubtext>
                  ) : (
                    <AnswerSubtext>-</AnswerSubtext>
                  )}
                </AnswerCell>
              </td>
              <td>
                <AnswerCell>
                  <AnswerValue>{formatHotline(surveyReport.q5_hotline_aware)}</AnswerValue>
                </AnswerCell>
              </td>
              <td>
                <ReportBadge type={getReportStatus(surveyReport.q5_hotline_aware)}>
                  {getBadgeLabel(getReportStatus(surveyReport.q5_hotline_aware))}
                </ReportBadge>
              </td>
            </tr>
            <tr>
              <td>
                <QuestionLabel>
                  <QuestionText>Q6: Kênh liên hệ</QuestionText>
                </QuestionLabel>
              </td>
              <td>
                <AnswerCell>
                  {surveyReport.q6_answer_text ? (
                    <AnswerSubtext style={{ color: '#374151', fontSize: 13, fontStyle: 'italic' }}>"{surveyReport.q6_answer_text}"</AnswerSubtext>
                  ) : (
                    <AnswerSubtext>-</AnswerSubtext>
                  )}
                </AnswerCell>
              </td>
              <td>
                <AnswerCell>
                  <AnswerValue>{formatChannel(surveyReport.q6_contact_channel)}</AnswerValue>
                </AnswerCell>
              </td>
              <td>
                <ReportBadge type={getReportStatus(surveyReport.q6_contact_channel)}>
                  {getBadgeLabel(getReportStatus(surveyReport.q6_contact_channel))}
                </ReportBadge>
              </td>
            </tr>
          </ReportTbody>
        </ReportTable>
      </ReportContainer>
    );
  };

  const renderRightSectionContent = () => {
    const phaseText = getCallPhaseText();
    if (isNewsVisible || (isNewsPlayingOnMap && !isMapVisible)) {
      return <News ref={newsContentElementRef} {...newsProps} />;
    }
    if (isMapVisible) {
      return (
        <MapContainer>
          <Map
            onMapLoad={mapManager.handleMapLoad}
            searchQuery={mapManager.mapSearchQuery}
            onLocationSelect={mapManager.handleLocationSelect}
            onCurrentLocationFound={mapManager.handleCurrentLocationFound}
            destination={mapManager.currentNavigationDestination || undefined}
            autocompleteSearch={mapManager.autocompleteSearch || ""}
            travelMode="DRIVING"
            locationChoices={locationChoices}
            onLocationChoiceSelect={(choice) => {
              if (choice.display) {
                mapManager.setCurrentNavigationDestination(choice.display);
                setLocationChoices([]);
              }
            }}
          />
        </MapContainer>
      );
    }
    // Show conversation with tabs when not showing map or news
    return (
      <ConversationContainer>
        <TabsContainer>
          <Tab
            active={activeTab === 'guide'}
            onClick={() => setActiveTab('guide')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
            </svg>
            {phaseText.tabGuide}
          </Tab>
          <Tab
            active={activeTab === 'conversation'}
            onClick={() => setActiveTab('conversation')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            {phaseText.tabConversation}
          </Tab>
        </TabsContainer>

        {activeTab === 'guide' ? (
          <GuidePane>
            {useCaseGuide ? (
              <UseCaseGuideMarkdown>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {useCaseGuide}
                </ReactMarkdown>
              </UseCaseGuideMarkdown>
            ) : (
              <GuideEmpty>{phaseText.emptyGuide}</GuideEmpty>
            )}
          </GuidePane>
        ) : activeTab === 'conversation' ? (
          <ConversationMessages ref={conversationMessagesRef}>
            {conversationHistory.length === 0 && !isThinking && !isSpeaking ? (
              <EmptyConversation>
                Chưa có tin nhắn. Hãy bắt đầu trò chuyện với AI Assistant.
              </EmptyConversation>
            ) : (
              <>
                {conversationHistory.map((message, index) => (
                  <MessageBubble key={index} role={message.role} isPending={message.isPending}>
                    <MessageRoleWrapper role={message.role}>
                      <MessageRoleIcon role={message.role}>
                        {message.role === "user" ? "👤" : "🤖"}
                      </MessageRoleIcon>
                      <MessageRole role={message.role}>
                        {message.role === "user" ? "Khách hàng" : "AI Agent"}
                      </MessageRole>
                      <MessageRoleBadge role={message.role}>
                        {message.role === "user" ? "Human" : "Bot"}
                      </MessageRoleBadge>
                    </MessageRoleWrapper>
                    {message.isPending ? "..." : message.content}
                  </MessageBubble>
                ))}
                {isThinking && !isSpeaking && (
                  <MessageBubble role="assistant">
                    <MessageRoleWrapper role="assistant">
                      <MessageRoleIcon role="assistant">🤖</MessageRoleIcon>
                      <MessageRole role="assistant">AI Agent</MessageRole>
                      <MessageRoleBadge role="assistant">Bot</MessageRoleBadge>
                    </MessageRoleWrapper>
                    <ThinkingText>
                      đang suy nghĩ
                      <ThinkingDots>
                        <span className="dot">.</span>
                        <span className="dot">.</span>
                        <span className="dot">.</span>
                      </ThinkingDots>
                    </ThinkingText>
                  </MessageBubble>
                )}
              </>
            )}
          </ConversationMessages>
        ) : (
          renderSurveyReport()
        )}
      </ConversationContainer>
    );
  };

  const renderMobileBottomBarContent = () => {
    if (isThinking) {
      return renderThinkingContent(true);
    }
    if (isNewsPlayingOnMap && currentNewsTitle && isMapVisible) {
      return <News {...newsProps} isMobileMapView={true} />;
    }
    // Don't show TTS content here - only show in conversation history
    return null;
  };

  const statusContent = (
    <StatusContent>
      <StatusRow>
        <StatusLabel>WebSocket:</StatusLabel>
        <StatusValue isConnected={webSocketAudioManager.isConnected}>
          {webSocketAudioManager.isConnected ? "Connected" : "Disconnected"}
        </StatusValue>
      </StatusRow>
      {webSocketAudioManager.clientId && (
        <StatusRow>
          <StatusLabel>Client ID:</StatusLabel>
          <ClientIdValue>{webSocketAudioManager.clientId}</ClientIdValue>
        </StatusRow>
      )}
    </StatusContent>
  );

  if (isInitializing)
    return (
      <LoadingOverlay>
        <LoadingSpinner />
        <LoadingText>Loading...</LoadingText>
      </LoadingOverlay>
    );

  // Floating gear button + drawer that overlay the initial/ringing
  // fullscreen screens. Lives outside the main TopBar because those
  // phases early-return and bypass TopBar entirely.
  const phoneSimFloatingButton = (
    <>
      <div
        onClick={() => setSettingsOpen(true)}
        title="Phone simulation settings"
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 10001,
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 12px",
          background: "rgba(0, 0, 0, 0.45)",
          borderRadius: 999,
          color: phoneSimMode !== "off" ? "#22d3ee" : "#fff",
          cursor: "pointer",
          fontSize: 13,
          backdropFilter: "blur(6px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <SettingOutlined style={{ fontSize: 18 }} />
        <span style={{ lineHeight: 1 }}>
          STT {sttModelBadge(sttModel)}
          {phoneSimMode !== "off" ? ` · ${phoneSimMode}` : ""}
        </span>
      </div>
      <PhoneSimSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        filterMode={phoneSimMode}
        setFilterMode={setPhoneSimMode}
        filterConfig={phoneSimConfig}
        setFilterConfig={setPhoneSimConfig}
        inputSampleRate={phoneSimInputRate}
        setInputSampleRate={setPhoneSimInputRate}
        sttModel={sttModel}
        setSttModel={setSttModel}
        callConnected={!!webSocketAudioManager.isConnected}
      />
    </>
  );

  // Show initial or ringing fullscreen overlay
  if (callPhase === 'initial') {
    return (
      <>
        {renderInitialScreen()}
        {phoneSimFloatingButton}
        <Notification
          {...notificationState}
          setNotificationState={setNotificationState}
        />
      </>
    );
  }

  if (callPhase === 'ringing') {
    return (
      <>
        {renderRingingScreen()}
        {phoneSimFloatingButton}
        <Notification
          {...notificationState}
          setNotificationState={setNotificationState}
        />
      </>
    );
  }

  // Block the entire app behind a name-entry modal on first visit
  if (!testerName) {
    return (
      <Container>
        <ModalOverlay>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Chào bạn!</ModalTitle>
            <ModalDescription>
              Vui lòng nhập tên của bạn trước khi bắt đầu. Tên sẽ được gắn vào các report và lưu lại cho lần sau.
            </ModalDescription>
            <ModalInput
              type="text"
              placeholder="Tên của bạn"
              value={testerNameInput}
              onChange={(e) => setTesterNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveTesterName();
              }}
              autoFocus
              maxLength={100}
            />
            <ModalButtonRow>
              <ModalButton
                primary
                onClick={handleSaveTesterName}
                disabled={!testerNameInput.trim()}
              >
                Lưu
              </ModalButton>
            </ModalButtonRow>
          </ModalCard>
        </ModalOverlay>
      </Container>
    );
  }

  return (
    <Container>
      <TopBar isMobileMapView={isMobile && isMapVisible}>
        <Popover
          content={statusContent}
          title={<span style={{ color: COLORS.white }}>Connection Status</span>}
          trigger={["hover", "click"]}
          placement="bottomLeft"
          arrow={false}
          overlayInnerStyle={{
            background: "rgba(0, 0, 0, 0.9)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          }}
          overlayStyle={{
            zIndex: 10001,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <InfoIcon />{" "}
            <span style={{ lineHeight: 1 }}>
              {webSocketAudioManager.clientId}
            </span>
          </div>
        </Popover>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            paddingLeft: 12,
            color: phoneSimMode !== "off" ? "#22d3ee" : COLORS.gray,
            fontSize: 13,
            transition: "all 0.3s ease",
          }}
          onClick={() => setSettingsOpen(true)}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLDivElement).style.color = COLORS.primary;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLDivElement).style.color =
              phoneSimMode !== "off" ? "#22d3ee" : COLORS.gray;
          }}
          title="Phone simulation & STT settings"
        >
          <SettingOutlined style={{ fontSize: 18 }} />
          <span style={{ lineHeight: 1 }}>
            STT {sttModelBadge(sttModel)}
            {phoneSimMode !== "off" ? ` · ${phoneSimMode}` : ""}
          </span>
        </div>
        {!isShared && (
          <ShareButton
            type="button"
            onClick={handleShare}
            title="Sao chép link chia sẻ"
          >
            <ShareAltOutlined />
            <span>Chia sẻ</span>
          </ShareButton>
        )}
      </TopBar>
      <PhoneSimSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        filterMode={phoneSimMode}
        setFilterMode={setPhoneSimMode}
        filterConfig={phoneSimConfig}
        setFilterConfig={setPhoneSimConfig}
        inputSampleRate={phoneSimInputRate}
        setInputSampleRate={setPhoneSimInputRate}
        sttModel={sttModel}
        setSttModel={setSttModel}
        callConnected={!!webSocketAudioManager.isConnected}
      />
      <MainLayout>
        <LeftSection>
          {shouldShowOrb && renderOrb()}
          {renderLeftSectionContent()}
        </LeftSection>

        <RightSection>
          {renderRightSectionContent()}
          {isMobile && shouldShowOrb && renderMobileControls()}
        </RightSection>
      </MainLayout>

      <Notification
        {...notificationState}
        setNotificationState={setNotificationState}
      />

      {connectionNotification}

      <CountdownEffect
        key={timeoutManager.countdownKey}
        timeoutActive={timeoutManager.timeoutActive}
        isThinking={isThinking}
        onTimeout={() => {
          // Play disconnect sound when timeout
          const disconnectSound = new Audio("/call disconnect.wav");
          disconnectSound.play().catch((err) => console.warn("Could not play disconnect sound:", err));

          wakewordManager.stopListening();
          isStartRef.current = false;
          setIsStart(false);
          // Tear down the WebSocket so the server stops billing/processing
          // and the .client.log carries an `idle_timeout` cause — without
          // this, the WS would linger until the next page event closes it
          // and the cause would mis-classify as `ws_abnormal_close` /
          // `page_unload`. Only run when the WS is still open; the timeout
          // can fire multiple times (re-armed across UI states) but
          // `disconnect()` is idempotent at the canonical-event level.
          if (webSocketAudioManager.isConnected) {
            webSocketAudioManager.disconnect("idle_timeout", "countdown_expired");
          }
        }}
        onTimeoutComplete={timeoutManager.handleTimeoutComplete}
      />

      {reportModalOpen && (() => {
        const phaseText = getCallPhaseText();
        return (
          <ModalOverlay onClick={handleCloseReport}>
            <ModalCard onClick={(e) => e.stopPropagation()}>
              <ModalTitle>{phaseText.reportTitle}</ModalTitle>
              {displayedCallId && (
                <ModalSessionId>ID: {displayedCallId}</ModalSessionId>
              )}
              <ModalTextarea
                placeholder={phaseText.reportPlaceholder}
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                disabled={reportSubmitting}
                autoFocus
              />
              {reportStatus && (
                <ModalStatus kind={reportStatus.kind}>{reportStatus.msg}</ModalStatus>
              )}
              <ModalButtonRow>
                <ModalButton onClick={handleCloseReport} disabled={reportSubmitting}>
                  {phaseText.reportCancel}
                </ModalButton>
                <ModalButton
                  primary
                  onClick={handleSubmitReport}
                  disabled={reportSubmitting || !reportText.trim()}
                >
                  {reportSubmitting ? phaseText.reportSubmitting : phaseText.reportSubmit}
                </ModalButton>
              </ModalButtonRow>
            </ModalCard>
          </ModalOverlay>
        );
      })()}

      {testerNameModalOpen && (
        <ModalOverlay
          onClick={() => {
            // Don't allow dismissing on first visit (no name yet)
            if (testerName) setTesterNameModalOpen(false);
          }}
        >
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{testerName ? "Đổi tên người test" : "Chào bạn!"}</ModalTitle>
            <ModalDescription>
              Vui lòng nhập tên của bạn để gắn vào các report. Tên sẽ được lưu lại cho lần sau.
            </ModalDescription>
            <ModalInput
              type="text"
              placeholder="Tên của bạn"
              value={testerNameInput}
              onChange={(e) => setTesterNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveTesterName();
              }}
              autoFocus
              maxLength={100}
            />
            <ModalButtonRow>
              {testerName && (
                <ModalButton onClick={() => setTesterNameModalOpen(false)}>Hủy</ModalButton>
              )}
              <ModalButton
                primary
                onClick={handleSaveTesterName}
                disabled={!testerNameInput.trim()}
              >
                Lưu
              </ModalButton>
            </ModalButtonRow>
          </ModalCard>
        </ModalOverlay>
      )}

      {guidelineModalOpen && (() => {
        const lang = (i18n.language === "km" || i18n.language === "en") ? i18n.language : "vi";
        const scenarioName =
          (scenarioLabels[lang] || scenarioLabels.vi).scenarios[selectedScenario] ||
          selectedScenario;
        const ucLabel = selectedUseCase
          ? (selectedUseCase.description
              ? `${selectedUseCase.id} — ${selectedUseCase.description}`
              : selectedUseCase.id)
          : "(chưa chọn use case)";
        return (
          <ModalOverlay onClick={() => setGuidelineModalOpen(false)}>
            <ModalCard onClick={(e) => e.stopPropagation()}>
              <ModalTitle>{scenarioName} · {ucLabel}</ModalTitle>
              {useCaseGuide ? (
                <UseCaseDetailPanel style={{ maxHeight: "60vh" }}>
                  <UseCaseGuideMarkdown>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {useCaseGuide}
                    </ReactMarkdown>
                  </UseCaseGuideMarkdown>
                </UseCaseDetailPanel>
              ) : (
                <ModalDescription>
                  Không có chi tiết cho use case này. Vui lòng cập nhật sheet và bấm tải lại.
                </ModalDescription>
              )}
              <ModalButtonRow>
                <ModalButton onClick={() => reloadUseCases(selectedScenario)}>
                  Tải lại
                </ModalButton>
                <ModalButton primary onClick={() => setGuidelineModalOpen(false)}>
                  Đóng
                </ModalButton>
              </ModalButtonRow>
            </ModalCard>
          </ModalOverlay>
        );
      })()}
    </Container>
  );
};

export default CallCenter;
